import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { useLanguage } from "../contexts/LanguageContext";
import { useTheme } from "../contexts/ThemeContext";
import { api } from "../lib/api";
import { AiChat } from "./ai/AiChat";
// Sprint 1085 — QuickWinsWidget retired in favor of unified JourneyHero on Mission Control.
import { WalkthroughProvider } from "./WalkthroughOverlay";
import { WalkthroughNudge } from "./WalkthroughNudge";
import SidebarAISpendChip from "./video-gen/SidebarAISpendChip";
import { ContextualHelp } from "./ContextualHelp";
import { GlobalSearch } from "./GlobalSearch";
import { AiCreditsIndicator } from "./AiCreditsIndicator";
import ActiveJobsTray from "./ActiveJobsTray";
import ChangelogDrawer from "./ChangelogDrawer";
import { BrandSwitcher } from "./BrandSwitcher";
import { LiveClock } from "./shared/LiveClock";
import EngagementAlertsListener from "./dashboard/EngagementAlertsListener";
import { toast } from "sonner";
import LocaleNudgeToast from "./ui/LocaleNudgeToast";

const UserProfileModal = ({ user, onClose, onUpdated }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    whatsapp_phone: user?.whatsapp_phone || "",
    job_title: user?.job_title || "",
    location: user?.location || "",
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put("/users/me", form);
      onUpdated(res.data);
      toast.success(t("Profile updated"));
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center" onClick={onClose} data-testid="user-profile-modal-overlay">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative w-full max-w-sm mx-4 rounded-2xl border border-[var(--app-border)] bg-[var(--app-bg)] shadow-2xl"
        onClick={e => e.stopPropagation()} data-testid="user-profile-modal">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-base font-semibold text-[var(--app-text)]">{t("Quick Edit")}</h2>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[var(--app-surface)] text-[var(--app-text-muted)]" data-testid="profile-modal-close">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
          <div className="flex items-center gap-3 mb-6">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-12 h-12 rounded-full border-2 border-[var(--app-border)]" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[var(--app-accent)] to-[#BF5AF2] flex items-center justify-center text-white font-semibold text-lg">{user?.name?.[0]}</div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[var(--app-text)] truncate">{user?.email}</p>
              <p className="text-[10px] text-[var(--app-text-muted)]">{user?.role || "User"}{user?.job_title ? ` · ${user.job_title}` : ""}</p>
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-[var(--app-text-muted)] mb-1.5 block">{t("Name")}</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full h-9 px-3 text-sm rounded-lg bg-[var(--app-surface)] border border-[var(--app-border)] text-[var(--app-text)] focus:outline-none focus:border-blue-500/50 transition-colors"
                data-testid="profile-name-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-[var(--app-text-muted)] mb-1.5 block">{t("Job Title")}</label>
              <input value={form.job_title} onChange={e => setForm(f => ({ ...f, job_title: e.target.value }))}
                placeholder={t("e.g. Executive Producer")}
                className="w-full h-9 px-3 text-sm rounded-lg bg-[var(--app-surface)] border border-[var(--app-border)] text-[var(--app-text)] focus:outline-none focus:border-blue-500/50 transition-colors"
                data-testid="profile-jobtitle-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-[var(--app-text-muted)] mb-1.5 block">{t("Location")}</label>
              <input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                placeholder={t("City, Country")}
                className="w-full h-9 px-3 text-sm rounded-lg bg-[var(--app-surface)] border border-[var(--app-border)] text-[var(--app-text)] focus:outline-none focus:border-blue-500/50 transition-colors"
                data-testid="profile-location-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-[var(--app-text-muted)] mb-1.5 block">{t("Phone")}</label>
              <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                placeholder="+1 555 123 4567"
                className="w-full h-9 px-3 text-sm rounded-lg bg-[var(--app-surface)] border border-[var(--app-border)] text-[var(--app-text)] focus:outline-none focus:border-blue-500/50 transition-colors"
                data-testid="profile-phone-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-[var(--app-text-muted)] mb-1.5 block">{t("WhatsApp")}</label>
              <input value={form.whatsapp_phone} onChange={e => setForm(f => ({ ...f, whatsapp_phone: e.target.value }))}
                placeholder="+52 55 1234 5678"
                className="w-full h-9 px-3 text-sm rounded-lg bg-[var(--app-surface)] border border-[var(--app-border)] text-[var(--app-text)] focus:outline-none focus:border-blue-500/50 transition-colors"
                data-testid="profile-whatsapp-input" />
            </div>
          </div>
          <div className="flex gap-2 mt-6">
            <button onClick={onClose}
              className="flex-1 h-9 rounded-lg text-xs font-medium bg-[var(--app-surface)] border border-[var(--app-border)] text-[var(--app-text-muted)] hover:bg-[var(--app-border)] transition-colors"
              data-testid="profile-cancel-btn">{t("Cancel")}</button>
            <button onClick={handleSave} disabled={saving}
              className="flex-1 h-9 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
              data-testid="profile-save-btn">{saving ? t("Saving...") : t("Save")}</button>
          </div>
          <button onClick={() => { onClose(); navigate("/settings"); }}
            className="w-full mt-3 text-[10px] text-[var(--app-text-muted)] hover:text-[var(--app-text)] text-center py-1.5 transition-colors"
            data-testid="profile-full-link">
            {t("View full profile in Settings")}
          </button>
          <button onClick={async () => {
              try { await api.post("/auth/logout"); window.location.href = "/login"; } catch { window.location.href = "/login"; }
            }}
            className="w-full mt-2 h-9 rounded-lg text-xs font-medium bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 hover:text-red-300 transition-colors"
            data-testid="profile-signout-btn">
            {t("Sign Out")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

import { PresenceIndicator } from "./PresenceIndicator";
import MarketplaceNewBanner from "./marketplace/MarketplaceNewBanner";
import TrialStatusBanner from "./TrialStatusBanner";
// Sprint 1069.C — Universal Flow Breadcrumb at the top of every mission page.
import FlowBreadcrumb from "./loop/FlowBreadcrumb";
import BrandStudioBreadcrumb from "./BrandStudioBreadcrumb";
// Sprint 2026-06-05 PM-7 — Row 1 of the canonical Brand Studio 3-row
// breadcrumb stack (Pillars: Foundation → Protect → Strengthen → Brand Health → Steward).
import BrandStudioPillarsBreadcrumb from "./loop/BrandStudioPillarsBreadcrumb";
import SalesStudioPillarsBreadcrumb from "./loop/SalesStudioPillarsBreadcrumb";
import MarketingStudioPillarsBreadcrumb from "./loop/MarketingStudioPillarsBreadcrumb";
import OperateAsBanner from "./OperateAsBanner";
import CloseDealBanner from "./CloseDealBanner";
// Compass Phase 3.6 — super-admin-only live doc-backlog badge.
import { CompassProgressBadge } from "./CompassProgressBadge";
// Sprint 1069.E1-followup-3 — Cross-Studio ⌘. Glance modal.
import GlanceModal from "./loop/GlanceModal";
// Sprint 1070.C5-prep6 — Mission/stage resolver for "You are here" tooltips.
import { pathToMissionStage } from "./loop/FlowBreadcrumb";
// Sprint 1070.C5-followup-2 — Canonical Web Studio taxonomy (single source of
// truth). The global sidebar generates its "Web Studio" group from this so
// the two never drift.
import { buildWebStudioSidebar } from "../config/webStudioShell";

/**
 * Sprint 1070.C5-followup-2 — Flatten the canonical web-studio shell sidebar
 * (groups + items) into Layout.jsx's path-list shape (separator + path rows).
 * Keeps the global sidebar in lockstep with the in-studio left rail.
 */
function buildWebStudioItemsForGlobalSidebar() {
  const groups = buildWebStudioSidebar();
  // SVG path strings used by Layout.jsx for icon rendering. We pick a
  // single representative path per group so the global sidebar stays
  // visually consistent (no lucide JSX inside the items array).
  const SVG = {
    home:     "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6",
    template: "M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10",
    bot:      "M13 10V3L4 14h7v7l9-11h-7z",
    upload:   "M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12",
    rocket:   "M4.5 12.75l6 6 9-13.5",
    globe:    "M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9",
    file:     "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
    plug:     "M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1",
    activity: "M3 4h18l-7 9v7l-4 2v-9L3 4z",
    cart:     "M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z",
    mail:     "M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z",
  };
  const iconForKey = {
    "create-templates":   SVG.template,
    "create-cobuild":     SVG.bot,
    "create-import":      SVG.upload,
    "go-live-publish":    SVG.rocket,
    "go-live-domains":    SVG.globe,
    "maintain-published": SVG.globe,
    "maintain-drafts":    SVG.file,
    "maintain-blog":      SVG.file,
    "integrate-hub":      SVG.globe,
    "integrate-connect":  SVG.plug,
    "integrate-funnel":   SVG.activity,
    "widgets-catalog":    SVG.cart,
    "widgets-newsletter": SVG.mail,
    "widgets-shop":       SVG.cart,
  };
  const out = [
    { path: "/web-studio", label: "Web Studio Home", label_es: "Inicio del Estudio Web", label_de: "Web-Studio Start", icon: SVG.home },
  ];
  for (const group of groups) {
    out.push({ separator: true, label: group.label });
    for (const item of group.items) {
      out.push({
        path: item.href,
        label: item.label,
        label_es: item.label,
        label_de: item.label,
        icon: iconForKey[item.key] || SVG.globe,
      });
    }
  }
  return out;
}

/**
 * Sprint 1070.C5-prep6 — Mission display map for "You are here" hovers.
 * Keeps the tooltip readable: short mission tagline + stage label.
 */
const MISSION_LABEL_FOR_TOOLTIP = {
  "win-the-client":   "Win the Client",
  "grow-the-brand":   "Grow the Brand",
  "run-the-project":  "Run the Project",
  "get-paid":         "Get Paid",
};
const STAGE_LABEL_FOR_TOOLTIP = {
  lead: "Lead", concept: "Concept", quote: "Quote", proposal: "Proposal", pitch: "Pitch", win: "Win",
  diagnose: "Diagnose", position: "Position", build: "Build", plan: "Plan",
  create: "Create", publish: "Publish", measure: "Measure", improve: "Improve",
  production: "Production", deliver: "Deliver",
  invoice: "Invoice", paid: "Paid",
};

function buildYouAreHereTooltip(label, linkPath) {
  const detected = pathToMissionStage((linkPath || "").split("?")[0]);
  if (!detected) return `📍 You are here · ${label}`;
  const mission = MISSION_LABEL_FOR_TOOLTIP[detected.mission] || detected.mission;
  const stage = STAGE_LABEL_FOR_TOOLTIP[detected.stage] || detected.stage;
  return `📍 You are here · ${label} · ${mission}: ${stage}`;
}

/**
 * Sprint 1070.C5-prep5 — Query-aware sidebar active-state matcher.
 *
 * Returns specificity (number of matching query params) if the link is a
 * candidate for "active", or null if it's not. The caller picks the link
 * with the HIGHEST specificity in each group (so a link with explicit
 * `?section=widgets` beats a generic `/web-studio` link when the URL is
 * `/web-studio?section=widgets`).
 */
function getLinkMatchScore(linkPath, currentPathname, currentSearch) {
  if (!linkPath) return null;
  const [linkPathname, linkSearch = ""] = linkPath.split("?");
  if (linkPathname !== currentPathname) return null;
  if (!linkSearch) return 0;
  const linkParams = new URLSearchParams(linkSearch);
  const currentParams = new URLSearchParams(currentSearch || "");
  let specificity = 0;
  for (const [k, v] of linkParams) {
    if (currentParams.get(k) !== v) return null;
    specificity += 1;
  }
  return specificity;
}

/**
 * Given a group of sidebar items + current URL, return the `path` of the
 * BEST-matching item (highest specificity). The caller compares each item
 * against this to render `isActive`.
 */
function bestActiveLinkInGroup(items, currentPathname, currentSearch) {
  let bestPath = null;
  let bestScore = -1;
  for (const item of items || []) {
    if (!item || item.separator || !item.path) continue;
    const score = getLinkMatchScore(item.path, currentPathname, currentSearch);
    if (score === null) continue;
    if (score > bestScore) {
      bestScore = score;
      bestPath = item.path;
    }
  }
  return bestPath;
}

const Sidebar = ({ mobileOpen, setMobileOpen, presenceUsers }) => {
  const location = useLocation();
  const { user, setUser } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const { themeName, toggleTheme, sidebarThemeName, setSidebarTheme, sidebarColors } = useTheme();
  const sc = sidebarColors; // sidebar color shorthand
  const [reminders, setReminders] = useState([]);
  const [showReminders, setShowReminders] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [agencyEnabled, setAgencyEnabled] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState(() => {
    try { return JSON.parse(localStorage.getItem("sidebar-collapsed") || "{}"); } catch { return {}; }
  });

  // Sprint 1090 follow-up — Full-rail collapse-to-icon-only mode.
  // Was promised in SPRINT_1069_PRD.md L232 but never actually shipped
  // (the existing collapsedSections only governs section groups, not the
  // entire rail). Persisted to localStorage so paco's preference survives
  // refreshes; default = expanded (matches Sprint 1069 PRD §7 recommendation
  // "expanded by default; persisted once user toggles").
  const [railCollapsed, setRailCollapsed] = useState(() => {
    try { return localStorage.getItem("sp:sidebar-rail-collapsed") === "1"; } catch { return false; }
  });
  const toggleRail = useCallback(() => {
    setRailCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem("sp:sidebar-rail-collapsed", next ? "1" : "0"); } catch {}
      try { document.documentElement.setAttribute("data-rail-collapsed", next ? "1" : "0"); } catch {}
      return next;
    });
  }, []);
  // Reflect initial state to the document so CSS / consumers can react.
  useEffect(() => {
    try { document.documentElement.setAttribute("data-rail-collapsed", railCollapsed ? "1" : "0"); } catch {}
  }, [railCollapsed]);

  // Sprint 937 — Tenant logo + name from Company Branding (with Studio Plaz fallback).
  // Sprint 938 — White-label sidebar tagline (Agency add-on / Enterprise only).
  // Public endpoint, no auth required, cached per session. Cache invalidated by
  // bumping `sp:tenant-brand-v` — Company Branding page bumps it on save/upload.
  const [tenantBrand, setTenantBrand] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("sp:tenant-brand") || "null"); } catch { return null; }
  });
  useEffect(() => {
    let cancelled = false;
    const cacheV = sessionStorage.getItem("sp:tenant-brand-v") || "0";
    const lastV = sessionStorage.getItem("sp:tenant-brand-fetched-v");
    // Skip refetch if we have cached data AND the version hasn't bumped.
    if (tenantBrand && cacheV === lastV) return;
    const refresh = async () => {
      try {
        const [pub, wl] = await Promise.all([
          api.get("/company-branding/public").catch(() => ({ data: {} })),
          api.get("/company-branding/whitelabel-status").catch(() => ({ data: { enabled: false } })),
        ]);
        if (cancelled) return;
        const d = pub.data || {};
        const w = wl.data || {};
        const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
        const resolve = (u) => (u && u.startsWith("/") ? `${API}${u}` : u);
        const next = {
          icon_url: resolve(d.logo_light_url) || resolve(d.logo_symbol_url) || resolve(d.logo_url) || null,
          name: d.company_name || null,
          // Sprint 938 — only honored when the tenant has Agency add-on / Enterprise tier.
          // Backend already gates: `sidebar_tagline` is "" if not entitled.
          tagline: (w.enabled && w.sidebar_tagline) ? w.sidebar_tagline : null,
          whitelabel_enabled: !!w.enabled,
        };
        setTenantBrand(next);
        try {
          sessionStorage.setItem("sp:tenant-brand", JSON.stringify(next));
          sessionStorage.setItem("sp:tenant-brand-fetched-v", cacheV);
        } catch {}
      } catch {/* fall through to Studio Plaz visuals */}
    };
    refresh();
    const onUpdate = () => {
      const v = String(Date.now());
      sessionStorage.setItem("sp:tenant-brand-v", v);
      sessionStorage.removeItem("sp:tenant-brand-fetched-v");
      refresh();
    };
    window.addEventListener("tenant-brand-updated", onUpdate);
    return () => { cancelled = true; window.removeEventListener("tenant-brand-updated", onUpdate); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSection = (label) => {
    setCollapsedSections(prev => {
      const next = { ...prev, [label]: !prev[label] };
      localStorage.setItem("sidebar-collapsed", JSON.stringify(next));
      return next;
    });
  };

  useEffect(() => {
    api.get("/reminders/upcoming").then((r) => {
      const data = r.data || {};
      const combined = [...(data.overdue || []).map(x => ({...x, is_overdue: true})), ...(data.upcoming || []).map(x => ({...x, is_overdue: false}))];
      setReminders(combined);
    }).catch(() => {});
    const interval = setInterval(() => {
      api.get("/reminders/upcoming").then((r) => {
        const data = r.data || {};
        const combined = [...(data.overdue || []).map(x => ({...x, is_overdue: true})), ...(data.upcoming || []).map(x => ({...x, is_overdue: false}))];
        setReminders(combined);
      }).catch(() => {});
    }, 120000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchNotifs = () => {
      api.get("/notifications").then((r) => {
        setNotifications(r.data?.notifications || []);
        setUnreadCount(r.data?.unread_count || 0);
      }).catch(() => {});
    };
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 60000);
    return () => clearInterval(interval);
  }, []);

  const markAllRead = () => {
    api.post("/notifications/mark-read", {}).then(() => {
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    }).catch(() => {});
  };

  useEffect(() => { setMobileOpen(false); }, [location.pathname, setMobileOpen]);

  // Sprint 1069.D2 — spatial-memory pulse. When the user navigates from a
  // studio shell tile (which sets `sp:sidebar-pulse-path`), briefly pulse the
  // matched sidebar link so they learn where it lives.
  // Sprint 1069.E1-followup-3 — also AUTO-EXPAND the parent group if it was
  // collapsed. Without this, a navigation from a Studio Card / Switcher Chip
  // would silently target a hidden link and the user'd never see the pulse.
  useEffect(() => {
    let pulsePath = null;
    try { pulsePath = sessionStorage.getItem("sp:sidebar-pulse-path"); } catch {}
    if (!pulsePath || pulsePath !== location.pathname) return;
    try { sessionStorage.removeItem("sp:sidebar-pulse-path"); } catch {}

    // 1) Find the parent group label that owns this path and force-expand it.
    const parentGroup = sidebarGroups.find(g =>
      g.label && g.items?.some(it => !it.separator && it.path === pulsePath)
    );
    let expandedNow = false;
    if (parentGroup) {
      setCollapsedSections(prev => {
        if (!prev[parentGroup.label]) return prev; // already open
        expandedNow = true;
        const next = { ...prev, [parentGroup.label]: false };
        try { localStorage.setItem("sidebar-collapsed", JSON.stringify(next)); } catch {}
        return next;
      });
    }

    // 2) Pulse the matched link (slight delay if we just expanded, so the
    //    DOM has time to mount the link node before we query for it).
    const pulse = () => {
      const el = document.querySelector(`[data-sidebar-link="${pulsePath}"]`);
      if (!el) return null;
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      el.classList.add("sidebar-pulse");
      return setTimeout(() => el.classList.remove("sidebar-pulse"), 2000);
    };
    let tid;
    if (expandedNow) {
      const settleTid = setTimeout(() => { tid = pulse(); }, 80);
      return () => { clearTimeout(settleTid); if (tid) clearTimeout(tid); };
    }
    tid = pulse();
    return () => { if (tid) clearTimeout(tid); };
    // sidebarGroups intentionally omitted — it's recomputed each render and
    // we want the latest snapshot when the effect fires.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Agency add-on entitlement check (unlocks /agency-dashboard sidebar link)
  useEffect(() => {
    let active = true;
    const fetchAgency = () => {
      api.get("/agency/status")
        .then(r => { if (active) setAgencyEnabled(!!r.data?.enabled); })
        .catch(() => { if (active) setAgencyEnabled(false); });
    };
    fetchAgency();
    const interval = setInterval(fetchAgency, 180000); // re-check every 3 min
    return () => { active = false; clearInterval(interval); };
  }, []);

  const overdueCount = reminders.filter((r) => r.is_overdue).length;
  const upcomingCount = reminders.filter((r) => !r.is_overdue).length;

  const isAdmin = user?.role === "System Admin";
  const isSuperAdmin = user?.is_super_admin === true;
  const perms = user?.permissions || {};
  /* Super-admins see every module — they own the platform. */
  const canAccess = (mod) => isSuperAdmin || (perms[mod] && perms[mod] !== "none");
  const canWrite = (mod) => isSuperAdmin || perms[mod] === "full";

  // Map label -> translation key for sidebar items
  const labelKey = {
    "Mission Control": "nav.mission_control",
    "Contacts": "nav.contacts",
    "Dashboard": "nav.dashboard", "Leads": "nav.leads", "Briefs": "nav.briefs",
    "Quotes": "nav.quotes", "Pipeline": "nav.pipeline", "Clients": "nav.clients",
    "Marketing": "nav.marketing", "Sales Calendar": "nav.sales_calendar",
    "Client Health": "nav.client_health",
    "Opportunities": "nav.opportunities", "Analytics": "nav.analytics",
    "Equipment": "nav.equipment", "Services": "nav.services", "Bundles": "nav.bundles",
    "Kits": "nav.kits", "Crew Directory": "nav.crew_directory", "Docs": "nav.docs",
    "Health + AI": "nav.health_ai", "Maint. Calendar": "nav.maint_calendar",
    "Gear Calendar": "nav.gear_calendar", "All Projects": "nav.all_projects",
    "Mailing Studio": "nav.mailing_system",
    "Marketing Hub": "nav.marketing_hub", "Brand Setup": "nav.brand_setup",
    "Brand Diagnostic": "nav.brand_diagnostic", "Brand Dashboard": "nav.brand_dashboard",
    "Marketing Plan": "nav.marketing_plan", "ROI Tracker": "nav.roi_tracker",
    "Website Builder": "nav.website_builder",
    "Gantt Chart": "nav.gantt_chart", "Project Calendar": "nav.project_calendar",
    "Projects Calendar": "nav.projects_calendar", "Shoots Calendar": "nav.shoots_calendar",
    "Resources": "nav.resources", "Reports": "nav.reports",
    "My Tasks": "nav.my_tasks", "AI Reports": "nav.ai_reports",
    "Financials": "nav.financials",
    "Reports & Briefings": "nav.reports_briefings", "Invoices": "nav.invoices",
    "Contracts": "nav.contracts", "Forms": "nav.intake_forms", "Rate Cards": "nav.rate_cards",
    // Forms Engine · Wave 2 Track 1 — new Studio mount-point entry-points.
    "Project Forms": "nav.project_forms",
    "Pulse & Surveys": "nav.pulse_surveys",
    "Research Surveys": "nav.research_surveys",
    // Forms Engine · Wave 2 Track 3 — Control Room sidebar entry.
    "Engines": "nav.engines",
    "Forms Engine": "nav.forms_engine",
    "Admin Calendar": "nav.admin_calendar",
    "Settings": "nav.settings", "Email Sync": "nav.email_sync", "Terms & Cond.": "nav.terms",
    "Company Branding": "nav.company_branding",
    "Proposal Builder": "nav.proposal_builder",
    "One Page Pitches": "nav.one_page_pitches",
    "Packages": "nav.packages",
    "Template Marketplace": "nav.template_marketplace",
    "Industry Templates": "nav.industry_templates",
    "Brand Launch": "nav.brand_launch",
    "Market Researcher": "nav.market_researcher",
    "Competitor Intel": "nav.competitor_intel",
    "SEO Analyzer": "nav.seo_analyzer",
    "AI Positioning": "nav.ai_positioning",
    "Social Content Hub": "nav.social_content_hub",
    "Brand Repository": "nav.brand_repository",
    "Skill Matrix": "nav.skill_matrix",
    "Kickoffs": "nav.kickoffs",
    "Calendar Feeds": "nav.calendar_feeds",
    "System Audit": "nav.system_audit",
    "Component Map": "nav.component_map",
    "Email Logs": "nav.email_logs",
    "Localization Hub": "nav.localization_hub",
    "Monetization": "nav.monetization",
    "Ops Dashboard": "nav.ops_dashboard",
    "Permissions": "nav.permissions",
    "AI Usage": "nav.ai_usage",
    "Storage Hub": "nav.storage_hub",
    "Control Room": "sidebar.control_room",
  };
  const sectionKey = {
    "Sales": "sidebar.sales", "Marketing": "sidebar.marketing", "Education": "sidebar.education", "Catalog": "sidebar.catalog", "Projects": "sidebar.projects",
    "Administration": "sidebar.administration", "System": "sidebar.system", "Control Room": "sidebar.control_room",
  };
  const sepKey = {
    "Proposal Studio": "sep.proposal_studio", "AI Sales": "sep.ai_sales",
    "Marketing Journey": "sep.marketing_journey", "Marketing Research": "sep.marketing_research",
    "Mailing & Web Design": "sep.mailing_web_design", "Catalog Calendar": "sep.catalog_calendar",
    "AI Catalog": "sep.ai_catalog", "Income": "sep.income", "Legal": "sep.legal",
    "HR": "sep.hr", "Reports & AI": "sep.reports_ai",
  };

  // Color coding per section group
  const groupColors = {
    "Sales": "var(--app-blue)",
    "Marketing": "var(--app-pink)",
    "Education": "var(--app-amber)",
    "Catalog": "var(--app-green)",
    "Projects": "var(--app-purple)",
    "Administration": "var(--app-amber)",
    "System": "var(--app-text-muted)",
    "Platform Tools": "#f43f5e",
    "Control Room": "#f43f5e",
  };

  const sidebarGroups = [
    {
      module: "dashboard",
      items: [
        { path: "/dashboard", label: "Mission Control", label_es: "Centro de Control", icon: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" },
        // Sprint 1070.A2 — Finance Studio + Operate Studio promoted to top group below Mission Control
        // (per Sidebar Hygiene Rule + paco direction). Replaces the legacy "Financials" + "Administration"
        // hubs which were redundant entry points to the same data.
        { path: "/finance-studio", label: "Finance Studio", label_es: "Estudio Financiero", label_de: "Finanz-Studio", icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" },
        { path: "/operate-studio", label: "Operate Studio", label_es: "Estudio de Operaciones", label_de: "Betriebs-Studio", icon: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" },
        // Forms Engine · Wave 2 Track 1 (ADR-016) — Operate Studio mount-point.
        // Surfaces internal/operational form types (kickoff, handover, supplier
        // intake, retro, post-mortem, equipment checkout). Closes the last
        // open studio entry per Sprint 1095 audit (paco, 2026-06-04).
        { path: "/forms?studio=operations", label: "Internal Forms", label_es: "Formularios Internos", label_de: "Interne Formulare", icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" },
        { path: "/contacts", label: "Contacts", label_es: "Contactos", icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" },
        { path: "/creative-studio", label: "Creative Studio", label_es: "Estudio Creativo", label_de: "Kreativstudio", icon: "M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" },
        ...(agencyEnabled ? [{ path: "/agency-dashboard", label: "Agency Dashboard", label_es: "Panel de Agencia", label_de: "Agentur-Dashboard", icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" }] : []),
      ]
    },
    {
      label: "Marketplace", label_es: "Marketplace",
      // No module restriction — everyone sees Marketplace; only Admin item is admin-only
      items: [
        { path: "/marketplace", label: "Browse", label_es: "Explorar", icon: "M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z" },
        { path: "/seller-dashboard", label: "My Listings", label_es: "Mis Listados", icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" },
        { path: "/seller-payouts", label: "Payouts", label_es: "Pagos", icon: "M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" },
        { path: "/marketplace-admin", label: "Admin", label_es: "Admin", icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z", adminOnly: true },
      ]
    },
    {
      label: "Sales", label_es: "Ventas", label_de: "Vertrieb",
      module: "sales",
      items: [
        // Sprint 1069.D — Sales Studio shell as the canonical entry point.
        { path: "/sales-studio", label: "Sales Studio", label_es: "Estudio de Ventas", label_de: "Sales-Studio", icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" },
        { path: "/leads", label: "Leads", label_es: "Prospectos", label_de: "Leads", icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" },
        { path: "/briefs", label: "Briefs", label_es: "Briefs", label_de: "Briefings", icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" },
        { path: "/quotes", label: "Quotes", label_es: "Cotizaciones", label_de: "Angebote", icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" },
        { path: "/kanban", label: "Pipeline", label_es: "Pipeline", label_de: "Pipeline", icon: "M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7" },
        { path: "/intake-forms", label: "Forms", label_es: "Formularios", label_de: "Formulare", icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" },
        { path: "/calendar", label: "Sales Calendar", label_es: "Calendario de Ventas", label_de: "Vertriebskalender", icon: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" },
        { path: "/meetings", label: "CalendarHub", label_es: "CalendarHub", label_de: "CalendarHub", icon: "M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" },
        { separator: true, label: "Proposal Studio", label_es: "Estudio de Propuestas", label_de: "Proposal-Studio" },
        { path: "/proposal-builder", label: "Proposal Builder", label_es: "Constructor de Propuestas", label_de: "Proposal-Builder", icon: "M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" },
        { path: "/proposal-builder?filter=pitch", label: "Pitch Decks", label_es: "Pitch Decks", label_de: "Pitch-Decks", icon: "M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" },
        { path: "/one-page-pitches", label: "One Page Pitches", label_es: "Pitches de Una Página", label_de: "Ein-Seiten-Pitches", icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" },
        // Sprint 1069.E1-followup — Renamed from "Analytics" to disambiguate
        // from the AI Sales analytics entry below (was a duplicate label).
        { path: "/proposal-analytics", label: "Proposal Analytics", label_es: "Analítica de Propuestas", label_de: "Proposal-Analytik", icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" },
        { separator: true, label: "AI Sales", label_es: "IA de Ventas", label_de: "KI-Vertrieb" },
        { path: "/administration/health", label: "Client Health", label_es: "Salud del Cliente", label_de: "Kundengesundheit", icon: "M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" },
        { path: "/analytics", label: "Sales Analytics", label_es: "Analítica de Ventas", label_de: "Vertriebs-Analytik", icon: "M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" },
      ]
    },
    {
      // Sprint 1065 — Project Development (Concept Room + downstream Decks/Deal Sheets deep-links).
      // Locked decision Q2=(a) — new top-level group under SALES (parallel,
      // NOT a replacement for the existing Proposal Studio surfaces).
      label: "Project Development", label_es: "Desarrollo de Proyecto", label_de: "Projektentwicklung",
      module: "sales",
      items: [
        { path: "/concept-room", label: "Concept Room", label_es: "Sala de Conceptos", label_de: "Konzeptraum", icon: "M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" },
        { path: "/proposal-builder", label: "Decks", label_es: "Decks", label_de: "Decks", icon: "M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" },
        { path: "/one-page-pitches", label: "Deal Sheets", label_es: "Hojas de Trato", label_de: "Deal Sheets", icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" },
        // Forms Engine · Wave 2 Track 1 (ADR-016) — Project Forms entry-point.
        // Single canonical Forms surface (/forms) filtered to projects-scope
        // taxonomies. Briefs remain authored under Sales (Lead→Intake→Brief).
        { path: "/forms?studio=projects", label: "Project Forms", label_es: "Formularios de Proyecto", label_de: "Projekt-Formulare", icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" },
      ]
    },
    {
      // ───────────────────────────────────────────────────────────────
      // Sprint Brand-Studio-Surface-Restore (2026-06-05)
      // Restores the dedicated Brand Studio sidebar group that was lost
      // when the 2026-06-04 Mission Architecture Restoration sprint
      // committed missionRegistry.js but never updated Layout.jsx.
      // Source of truth: BrandStudioPage.jsx SECTION_REGISTRY +
      // /app/memory/SYSTEM_AUDIT/brand-studio-canonical-architecture.md.
      // Mission #1 ("Build & Protect the Brand") — indigo #6366F1.
      // ───────────────────────────────────────────────────────────────
      label: "Brand Studio", label_es: "Estudio de Marca", label_de: "Marken-Studio",
      module: "sales",
      items: [
        { path: "/brand-studio", label: "Brand Studio", label_es: "Estudio de Marca", label_de: "Marken-Studio", icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" },
        { separator: true, label: "Foundation", label_es: "Cimientos", label_de: "Fundament" },
        { path: "/brand-studio/foundation/setup", label: "Brand Setup", label_es: "Configuración de Marca", label_de: "Marken-Setup", icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" },
        { path: "/brand-studio/foundation/identity", label: "Brand Identity", label_es: "Identidad de Marca", label_de: "Markenidentität", icon: "M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" },
        { path: "/brand-studio/foundation/voice", label: "Brand Voice", label_es: "Voz de Marca", label_de: "Markenstimme", icon: "M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" },
        { path: "/brand-studio/foundation/repository", label: "Brand Repository", label_es: "Repositorio de Marca", label_de: "Marken-Repository", icon: "M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" },
        { path: "/brand-studio/foundation/launch", label: "Brand Launch", label_es: "Lanzamiento de Marca", label_de: "Marken-Launch", icon: "M13 10V3L4 14h7v7l9-11h-7z" },
        { separator: true, label: "Protect", label_es: "Proteger", label_de: "Schützen" },
        { path: "/brand-studio/protect/identity-analysis", label: "Identity Analysis", label_es: "Análisis de Identidad", label_de: "Identitäts-Analyse", icon: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" },
        { path: "/brand-studio/protect/domain-portfolio", label: "Domain Portfolio", label_es: "Portafolio de Dominios", label_de: "Domain-Portfolio", icon: "M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" },
        { path: "/brand-studio/protect/trademark-readiness", label: "Trademark Readiness", label_es: "Listo para Marca", label_de: "Marken-Bereitschaft", icon: "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" },
        { path: "/brand-studio/protect/evidence-center", label: "Evidence Center", label_es: "Centro de Evidencia", label_de: "Beweis-Center", icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" },
        { separator: true, label: "Strengthen", label_es: "Fortalecer", label_de: "Stärken" },
        { path: "/brand-studio/strengthen/diagnostic", label: "Brand Diagnostic", label_es: "Diagnóstico de Marca", label_de: "Marken-Diagnose", icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" },
        { path: "/brand-studio/strengthen/positioning", label: "AI Positioning", label_es: "Posicionamiento IA", label_de: "KI-Positionierung", icon: "M13 10V3L4 14h7v7l9-11h-7z" },
        { path: "/brand-studio/strengthen/competitor", label: "Competitor Intel", label_es: "Inteligencia Competitiva", label_de: "Wettbewerber-Intel", icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" },
        { separator: true, label: "Health", label_es: "Salud", label_de: "Gesundheit" },
        { path: "/brand-studio/brand-health", label: "Brand Health", label_es: "Salud de Marca", label_de: "Markengesundheit", icon: "M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" },
        { path: "/brand-studio/brand-performance", label: "Brand Performance", label_es: "Rendimiento de Marca", label_de: "Marken-Performance", icon: "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" },
        { separator: true, label: "Steward", label_es: "Custodiar", label_de: "Verwalten" },
        { path: "/brand-studio/steward/living-bi", label: "Living Brand Intelligence", label_es: "Inteligencia de Marca Viva", label_de: "Lebendige Marken-Intelligenz", icon: "M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" },
        { separator: true, label: "Pulse", label_es: "Pulso", label_de: "Pulse" },
        // Forms Engine · Wave 2 Track 1 — Brand mount-point (filtered to brand-scoped forms).
        { path: "/forms?studio=brand", label: "Pulse & Surveys", label_es: "Pulso y Encuestas", label_de: "Pulse & Umfragen", icon: "M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" },
      ]
    },
    {
      label: "Marketing", label_es: "Marketing", label_de: "Marketing",
      module: "sales",
      items: [
        // Sprint 1069.E1 — Marketing Studio promoted as the FIRST item (per
        // Sidebar Hygiene Rule + Studio Shell Standard § 7).
        { path: "/marketing-studio", label: "Marketing Studio", label_es: "Estudio de Marketing", label_de: "Marketing-Studio", icon: "M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" },
        // Sprint Brand-Studio-Surface-Restore (2026-06-05) — Marketing
        // Journey now contains ONLY marketing-native items. Brand items
        // (Brand Setup, Brand Diagnostic, Brand Repository, Brand Launch,
        // Brand Dashboard) moved to the dedicated Brand Studio sidebar
        // group above. The grow-the-brand mission still references the
        // legacy /marketing/brand-* routes — those routes stay alive in
        // App.js for backward compat but are no longer surfaced here.
        { separator: true, label: "Marketing Journey", label_es: "Ruta de Marketing", label_de: "Marketing-Reise" },
        { path: "/marketing/plans", label: "Marketing Plan", label_es: "Plan de Marketing", label_de: "Marketing-Plan", icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" },
        { path: "/marketing/roi", label: "ROI Tracker", label_es: "Rastreador ROI", label_de: "ROI-Tracker", icon: "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" },
        { path: "/marketing/dashboard", label: "Marketing Hub", label_es: "Hub de Marketing", label_de: "Marketing-Hub", icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" },
        { separator: true, label: "Marketing Research", label_es: "Investigación de Marketing", label_de: "Marktforschung" },
        // Forms Engine · Wave 2 Track 1 (ADR-016) — Marketing mount-point.
        // Audience/campaign/market research surveys.
        { path: "/forms?studio=marketing", label: "Research Surveys", label_es: "Encuestas de Investigación", label_de: "Forschungs-Umfragen", icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" },
        { path: "/marketing/research", label: "Market Researcher", label_es: "Investigador de Mercado", label_de: "Marktforscher", icon: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" },
        { path: "/marketing/seo-analyzer", label: "SEO Analyzer", label_es: "Analizador SEO", label_de: "SEO-Analyzer", icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" },
        { separator: true, label: "Mailing & Web Design", label_es: "Mailing y Diseño Web", label_de: "Mailing & Web-Design" },
        { path: "/marketing", label: "Mailing Studio", label_es: "Estudio de Correo", label_de: "Mailing-Studio", icon: "M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" },
        { path: "/creative-studio?section=social", label: "Social Content Hub", label_es: "Hub de Contenido Social", label_de: "Social-Content-Hub", icon: "M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" },
        { path: "/platform/blog", label: "Blog", label_es: "Blog", label_de: "Blog", icon: "M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" },
        { path: "/web-studio", label: "Web Studio →", label_es: "Estudio Web →", label_de: "Web-Studio →", icon: "M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" },
        // Sprint 1069.E1-followup — API Keys + Integrations REMOVED from
        // Marketing (they are platform-level utilities and now live in the
        // Administration group). Brand Repository moved up to Marketing
        // Journey (it's a brand asset, not a mailing/web tool).
      ]
    },
    {
      // Sprint 1070.C5-followup-2 — Web Studio group items now generated from
      // the canonical `webStudioShell.js` taxonomy so the global sidebar and
      // the in-studio left rail can never drift apart again.
      label: "Web Studio", label_es: "Estudio Web", label_de: "Web-Studio",
      module: "marketing",
      items: buildWebStudioItemsForGlobalSidebar(),
    },
    {
      label: "Catalog", label_es: "Catálogo", label_de: "Katalog",
      module: "catalog",
      items: [
        // Sprint 1069.E2 — Catalog Studio promoted as the FIRST item (per Sidebar Hygiene Rule).
        { path: "/catalog-studio", label: "Catalog Studio", label_es: "Estudio de Catálogo", label_de: "Katalog-Studio", icon: "M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" },
        { path: "/catalog", label: "Equipment", label_es: "Equipos", label_de: "Ausrüstung", icon: "M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" },
        { path: "/catalog/services", label: "Services", label_es: "Servicios", label_de: "Services", icon: "M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" },
        { path: "/packages", label: "Packages", label_es: "Paquetes", label_de: "Pakete", icon: "M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" },
        { path: "/catalog/bundles", label: "Bundles", label_es: "Bundles", label_de: "Bundles", icon: "M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" },
        { path: "/catalog/kits", label: "Kits", label_es: "Kits", label_de: "Kits", icon: "M20 7l-8-4-8 4m16 0v10l-8 4M4 7v10l8 4m0-10l8-4M12 17l-8-4" },
        { separator: true, label: "Catalog Calendar", label_es: "Calendario de Catálogo", label_de: "Katalog-Kalender" },
        { path: "/catalog/maintenance-calendar", label: "Maint. Calendar", label_es: "Mantenimiento", label_de: "Wartungs-Kalender", icon: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" },
        { path: "/catalog/gear-calendar", label: "Gear Calendar", label_es: "Calendario de Equipos", label_de: "Geräte-Kalender", icon: "M4 6h16M4 10h16M4 14h16M4 18h16" },
        { separator: true, label: "AI Catalog", label_es: "Catálogo IA", label_de: "KI-Katalog" },
        { path: "/catalog/health", label: "Health + AI", label_es: "Salud + IA", label_de: "Gesundheit + KI", icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" },
        { separator: true, label: "Industry", label_es: "Industria", label_de: "Industrie" },
        // Sprint 1069.E1-followup-3 — Renamed "Template Gallery" → "Industry
        // Templates" per founder direction. The label now matches the section
        // header ("Industry") and disambiguates from other gallery surfaces.
        { path: "/industry-templates", label: "Industry Templates", label_es: "Plantillas de Industria", label_de: "Branchen-Vorlagen", icon: "M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" },
      ]
    },
    {
      label: "Projects", label_es: "Proyectos", label_de: "Projekte",
      module: "projects",
      items: [
        // Sprint 1069.E2 — Projects Studio promoted as the FIRST item (per Sidebar Hygiene Rule).
        { path: "/projects-studio", label: "Projects Studio", label_es: "Estudio de Proyectos", label_de: "Projekt-Studio", icon: "M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" },
        { path: "/projects", label: "All Projects", label_es: "Todos los Proyectos", label_de: "Alle Projekte", icon: "M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" },
        { path: "/crew-resources", label: "Resources", label_es: "Recursos", label_de: "Ressourcen", icon: "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" },
        { path: "/my-tasks", label: "My Tasks", label_es: "Mis Tareas", label_de: "Meine Aufgaben", icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" },
        { path: "/project-calendar", label: "Projects Calendar", label_es: "Calendario de Proyectos", label_de: "Projekt-Kalender", icon: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" },
        { path: "/calendar/production", label: "Shoots Calendar", label_es: "Calendario de Producción", label_de: "Dreh-Kalender", icon: "M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" },
        { path: "/projects/gantt", label: "Gantt Chart", label_es: "Diagrama de Gantt", label_de: "Gantt-Diagramm", icon: "M4 6h16M4 10h10M4 14h13M4 18h7" },
        { path: "/kickoffs", label: "Kickoffs", label_es: "Kickoffs", label_de: "Kickoffs", icon: "M13 10V3L4 14h7v7l9-11h-7z" },
        { path: "/reports", label: "Reports", label_es: "Reportes", label_de: "Berichte", icon: "M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" },
      ]
    },
    {
      label: "Back Office", label_es: "Trastienda", label_de: "Back Office",
      module: "administration",
      items: [
        // Sprint 1070.A2 — Finance Studio + Operate Studio promoted to top sidebar group.
        // Legacy "Financials" + "Administration" hub entries removed (replaced by the studios).
        // NAV-1 (2026-07-09) — Administration hub re-surfaced explicitly: /administration serves the full
        // finance-admin hub (PnL, Expenses, Revenue Goals, etc.); it must not be reachable only via Admin Calendar.
        { path: "/administration", label: "Administration", label_es: "Administración", label_de: "Verwaltung", icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" },
        { path: "/administration/calendar", label: "Admin Calendar", label_es: "Calendario Admin", icon: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" },
        { separator: true, label: "Income", label_es: "Ingresos" },
        { path: "/invoices", label: "Invoices", label_es: "Facturas", icon: "M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" },
        { separator: true, label: "Legal", label_es: "Legal" },
        { path: "/contracts", label: "Contracts", label_es: "Contratos", icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h4l2 2h4a2 2 0 012 2v12a2 2 0 01-2 2z" },
        { separator: true, label: "HR", label_es: "RRHH" },
        { path: "/crew", label: "Crew Directory", label_es: "Directorio de Equipo", icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" },
        { path: "/crew/skill-matrix", label: "Skill Matrix", label_es: "Matriz de Habilidades", icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" },
        { path: "/rate-cards", label: "Rate Cards", label_es: "Tarifas", icon: "M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" },
        { path: "/rate-intelligence", label: "Rate Intelligence", label_es: "Inteligencia de Tarifas", icon: "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" },
        { separator: true, label: "Reports & AI", label_es: "Reportes e IA", label_de: "Berichte & KI" },
        { path: "/reporting", label: "Reports & Briefings", label_es: "Reportes e Informes", label_de: "Berichte & Briefings", icon: "M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" },
        // Sprint 1069.E1-followup — 2026-05-27: Platform Tools (API Keys +
        // Integrations) moved OUT of Back Office and INTO the System group
        // per paco's audit (these are platform-config utilities, not
        // back-office workflow tools).
      ]
    },
    {
      // Sprint 1085 — Education repositioned between Back Office and System per paco's sidebar
      // ergonomics note (operational sections before utility/config sections).
      label: "Education", label_es: "Educación", label_de: "Bildung",
      module: "sales",
      items: [
        { path: "/platform/education", label: "Education Hub", label_es: "Hub Educativo", label_de: "Bildungszentrum", icon: "M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" },
        { path: "/clinic", label: "Client Clinic", label_es: "Clínica de Clientes", label_de: "Kundenklinik", icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" },
        { path: "/awards", label: "Industry Awards", label_es: "Premios de Industria", label_de: "Branchenpreise", icon: "M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" },
      ]
    },
    {
      label: "System", label_es: "Sistema",
      module: "system",
      items: [
        // 2026-05-27 — 4-subdivision reorg per paco's approval.
        // Top-to-bottom by "how often you touch it":
        //   1. Identity & Branding  (workspace identity, touched at onboarding)
        //   2. Configuration        (operational config — workflows, calendars, email)
        //   3. Platform Tools       (developer/integration surface)
        //   4. Learn & Grow         (Help Center, Ambassador — non-config)
        { separator: true, label: "Identity & Branding", label_es: "Identidad y Marca", label_de: "Identität & Marke" },
        // Sprint Brand-Studio-Surface-Restore (2026-06-05) — Phase 7 split
        // restored per BRAND_STUDIO_MIGRATION_AUDIT.md row 11.
        // The legacy `/company-branding` monolith was split into 4 single-
        // purpose homes (one moved to Brand Studio, three live here in System).
        // Underlying component is shared (CompanyBrandingPage) with new
        // `forcedTab` + `hideTabs` props. The legacy URL stays alive as a
        // transitional landing page.
        { path: "/system/company-profile", label: "Company Profile", label_es: "Perfil de la Empresa", label_de: "Unternehmensprofil", icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" },
        { path: "/system/legal/terms", label: "Terms & Conditions", label_es: "Términos y Condiciones", label_de: "Geschäftsbedingungen", icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" },
        { path: "/system/legal/documents", label: "Document Templates", label_es: "Plantillas de Documentos", label_de: "Dokumentvorlagen", icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" },
        { path: "/company-branding", label: "Branding (legacy)", label_es: "Marca (heredado)", label_de: "Branding (alt)", icon: "M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" },
        { path: "/settings", label: "Settings", label_es: "Configuración", icon: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" },

        { separator: true, label: "Configuration", label_es: "Configuración", label_de: "Konfiguration" },
        { path: "/journeys", label: "Data Flows", label_es: "Flujos de Datos", label_de: "Datenflüsse", icon: "M13 10V3L4 14h7v7l9-11h-7z" },
        { path: "/calendar/subscriptions", label: "Calendar Feeds", label_es: "Feeds de Calendario", icon: "M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9" },
        { path: "/email-ingest", label: "Email Sync", label_es: "Sincronización de Email", icon: "M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" },

        { separator: true, label: "Platform Tools", label_es: "Herramientas de Plataforma", label_de: "Plattform-Tools" },
        { path: "/platform/api-keys", label: "API Keys", label_es: "Claves de API", label_de: "API-Schlüssel", icon: "M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" },
        { path: "/platform/integrations", label: "Integrations", label_es: "Integraciones", label_de: "Integrationen", icon: "M13 10V3L4 14h7v7l9-11h-7z" },

        { separator: true, label: "Learn & Grow", label_es: "Aprender y Crecer", label_de: "Lernen & Wachsen" },
        { path: "/help", label: "Help Center", label_es: "Centro de Ayuda", icon: "M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" },
        { path: "/ambassador", label: "Ambassador Program", label_es: "Programa de Embajadores", label_de: "Botschafter-Programm", icon: "M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" },
      ]
    },
    ...(isSuperAdmin ? [{
      label: "Launch Hub", label_es: "Hub de Lanzamiento", label_de: "Launch-Hub",
      module: "system",
      items: [
        { path: "/platform/launch", label: "Launch Hub", label_es: "Hub de Lanzamiento", label_de: "Launch-Hub", icon: "M5 3l3.057-3 3.057 3M8.057 0v18m-3-3l3 3 3-3M3 8l-3 3 3 3M0 11h18m-3-3l3 3-3 3" },
        { path: "/platform/launch/growth", label: "Growth Dashboard", label_es: "Panel de Crecimiento", label_de: "Wachstums-Dashboard", icon: "M3 3v18h18M7 14l4-4 4 4 5-5" },
        { path: "/platform/launch-broadcast", label: "Launch Broadcast", label_es: "Difusión de Lanzamiento", icon: "M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" },
        { path: "/platform/nurture-drip", label: "Nurture Drip", label_es: "Goteo de Nutrición", icon: "M12 2v6M12 22v-6M2 12h6M22 12h-6M5 5l4 4M19 19l-4-4M5 19l4-4M19 5l-4 4" },
        { path: "/platform/launch/qr-codes", label: "QR Code Generator", label_es: "Generador de QR", icon: "M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h2v2h-2zM18 14h3v3h-3zM14 18h2v3h-2zM18 18h3v3h-3z" },
        { path: "/platform/launch/press-kit", label: "Press Kit", label_es: "Kit de Prensa", icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" },
        { path: "/platform/launch/changelog", label: "Changelog Editor", label_es: "Editor de Cambios", icon: "M12 4v16m8-8H4" },
        { path: "/platform/launch/leaderboard", label: "Referral Leaderboard", label_es: "Tabla de Referidos", icon: "M5 3v18l7-5 7 5V3z" },
        { path: "/platform/launch/config", label: "Countdown & A/B", label_es: "Cuenta Regresiva y A/B", icon: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" },
      ]
    }] : []),
    ...(isSuperAdmin ? [{
      label: "Control Room", label_es: "Sala de Control",
      module: "system",
      items: [
        // ── Overview
        { separator: true, label: "Overview" },
        { path: "/platform", label: "Control Room", label_es: "Sala de Control", icon: "M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" },
        { path: "/platform/admin-index", label: "Admin Index", label_es: "Índice Admin", label_de: "Admin-Index", icon: "M4 6h16M4 10h16M4 14h16M4 18h16" },
        { path: "/platform/ops-dashboard", label: "Ops Dashboard", label_es: "Panel de Operaciones", icon: "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" },
        { path: "/super-admin", label: "Super Admin", label_es: "Super Admin", icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" },
        // ── Knowledge & Docs
        { separator: true, label: "Knowledge" },
        { path: "/platform/dev-library", label: "Dev Library", label_es: "Biblioteca de Desarrollo", label_de: "Entwicklerbibliothek", icon: "M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" },
        { path: "/platform/system-audit", label: "System Audit", label_es: "Auditoría del Sistema", icon: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" },
        { path: "/platform/system-intelligence", label: "System Intelligence", label_es: "Inteligencia del Sistema", label_de: "System-Intelligenz", icon: "M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" },
        { path: "/platform/component-map", label: "Component Map", label_es: "Mapa de Componentes", icon: "M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" },
        { path: "/platform/links-registry", label: "Links Registry", label_es: "Registro de Enlaces", label_de: "Linkverzeichnis", icon: "M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" },
        // ── Operations & Storage
        { separator: true, label: "Operations" },
        // Mission Video Engine V3.1 — paco-only HITL marketing-video pipeline (Control Room → Operations)
        { path: "/platform/mission-video-studio", label: "Mission Video Studio", label_es: "Estudio de Video", label_de: "Mission-Video-Studio", icon: "M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 4h16a1 1 0 011 1v14a1 1 0 01-1 1H4a1 1 0 01-1-1V5a1 1 0 011-1z", paco_only: true },
        { path: "/platform/storage", label: "Storage Hub", label_es: "Centro de Almacenamiento", icon: "M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" },
        // Compass · 2026-05-21 — paco-exclusive R2 Screenshot Browser (gated by paco_only)
        { path: "/platform/r2-browser", label: "R2 Screenshot Library", label_es: "Biblioteca R2", label_de: "R2-Bibliothek", icon: "M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z", paco_only: true },
        { path: "/platform/permissions", label: "Permissions", label_es: "Permisos", icon: "M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" },
        { path: "/platform/integrations", label: "Integrations Vault", label_es: "Bóveda de Integraciones", label_de: "Integrations-Tresor", icon: "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" },
        { path: "/platform/support-tickets", label: "Support Tickets", label_es: "Tickets de Soporte", icon: "M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" },
        // ── Finance & Growth
        { separator: true, label: "Finance & Growth" },
        { path: "/platform/financial-cockpit", label: "Financial Cockpit", label_es: "Cabina Financiera", label_de: "Finanz-Cockpit", icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" },
        { path: "/platform/monetization", label: "Monetization", label_es: "Monetización", icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" },
        { path: "/platform/ai-usage", label: "AI Usage", label_es: "Uso de IA", icon: "M13 10V3L4 14h7v7l9-11h-7z" },
        // Sprint 1070.A9 — 1099 tax-season export (super-admin)
        { path: "/platform/finance/1099", label: "1099 Tax Export", label_es: "Exportación Fiscal 1099", label_de: "1099 Steuer-Export", icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" },
        // Sprint 1070.B3 — Quarter Recap (super-admin, LLM verdict)
        { path: "/platform/quarter-recap", label: "Quarter Recap", label_es: "Resumen Trimestral", label_de: "Quartalsbericht", icon: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" },
        // Sprint A.1 (2026-06-16) — Brand Asset Activity Timeline (super-admin, ADR-018 §A.5 chain-of-custody)
        { path: "/platform/brand-asset-activity", label: "Brand Asset Activity", label_es: "Actividad de Activos de Marca", label_de: "Brand-Asset-Aktivität", icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2M12 11v6m-3-3h6", paco_only: true },
        // ── Engines (Forms Engine · ADR-016 Wave 2 Track 3 sidebar mount)
        { separator: true, label: "Engines", label_es: "Motores", label_de: "Engines" },
        { path: "/platform/engines/forms", label: "Forms Engine", label_es: "Motor de Formularios", label_de: "Formular-Engine", icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" },
        // ── Localization
        { separator: true, label: "Localization" },
        { path: "/platform/localization", label: "Localization Hub", label_es: "Centro de Localización", icon: "M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" },
      ]
    }] : []),
  ];

  // Filter sidebar groups based on user permissions
  const ADMIN_EMAILS = new Set(["paco@baklight.com"]);
  const userEmail = (user?.email || "").toLowerCase();
  const isMarketplaceAdmin = ADMIN_EMAILS.has(userEmail);
  const filteredGroups = sidebarGroups
    .filter(g => !g.module || canAccess(g.module))
    .map(g => ({
      ...g,
      items: g.items.filter(l => (!l.adminOnly || isMarketplaceAdmin) && (!l.paco_only || isMarketplaceAdmin)),
    }));

  return (
    <>
      {/* Sprint 1069.E2 — Locale Nudge Toast: one-time browser-locale detection. */}
      <LocaleNudgeToast />
      {/* Mobile overlay */}
      {mobileOpen && <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />}
      <aside className={`${railCollapsed ? "w-16" : "w-64"} h-screen flex flex-col z-50 fixed lg:sticky lg:top-0 transition-[transform,width] duration-200 ease-out ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        style={{ backgroundColor: sc.bg, borderRight: `1px solid ${sc.border}` }}
        data-testid="sidebar"
        data-rail-collapsed={railCollapsed ? "1" : "0"}>
      {/* Sprint 1093-c — Re-open chevron pill rendered OUTSIDE the <aside>
          and portaled to <body> so the aside's `overflow-x: hidden` (used
          to clip leaky labels when collapsed) can't slice off the half of
          the button that sits past the rail's right edge. Position tracks
          the rail width via inline style. */}
      {createPortal(
        <button
          type="button"
          onClick={toggleRail}
          title={railCollapsed ? t("Expand sidebar") : t("Collapse sidebar")}
          aria-label={railCollapsed ? t("Expand sidebar") : t("Collapse sidebar")}
          aria-expanded={!railCollapsed}
          className={`sidebar-rail-toggle hidden lg:flex fixed w-7 h-7 rounded-full items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95 ${railCollapsed ? "sidebar-rail-toggle--pulse" : ""}`}
          style={{
            top: 16,
            // The rail is 64px (w-16) when collapsed, 256px (w-64) when
            // expanded. We center the 28px pill on the rail's right edge.
            left: (railCollapsed ? 64 : 256) - 14,
            backgroundColor: railCollapsed ? "#0070FF" : sc.bg,
            border: `1.5px solid ${railCollapsed ? "#0070FF" : sc.border}`,
            color: railCollapsed ? "#FFFFFF" : sc.textSecondary,
            boxShadow: railCollapsed
              ? "0 4px 14px -2px rgba(0,112,255,0.45), 0 0 0 4px rgba(0,112,255,0.12)"
              : "0 2px 6px -1px rgba(0,0,0,0.18)",
            zIndex: 60,
          }}
          data-testid="sidebar-rail-toggle"
          data-rail-state={railCollapsed ? "collapsed" : "expanded"}
        >
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.6"
            viewBox="0 0 24 24"
            style={{ transform: railCollapsed ? "rotate(180deg)" : "rotate(0)", transition: "transform 200ms ease-out" }}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>,
        document.body,
      )}
      <div className={`${railCollapsed ? "p-3" : "p-6"} transition-[padding] duration-200`} style={{ borderBottom: `1px solid ${sc.border}` }}>
        <div className="flex items-center gap-3" data-testid="sidebar-tenant-brand">
          <img
            src={tenantBrand?.icon_url || "/studio-plaz-icon.png"}
            alt={tenantBrand?.name || "Studio Plaz"}
            className="w-10 h-10 rounded-xl shadow-lg object-contain bg-white/5 shrink-0"
            onError={(e) => { e.currentTarget.src = "/studio-plaz-icon.png"; }}
            data-testid="sidebar-tenant-icon"
            title={railCollapsed ? (tenantBrand?.name || "Studio Plaz") : undefined}
          />
          {!railCollapsed && (
            <div className="min-w-0">
              <h1 className="text-lg font-bold truncate" style={{ color: sc.text }} data-testid="sidebar-tenant-name">
                {tenantBrand?.name || t("Studio Plaz")}
              </h1>
              <p className="text-xs" style={{ color: sc.textMuted }} data-testid="sidebar-tagline">{tenantBrand?.tagline || t("Studio")}</p>
            </div>
          )}
        </div>
      </div>

      <nav className="flex-1 px-3 py-2 overflow-y-auto" data-testid="sidebar-nav">
        {filteredGroups.map((group, gi) => {
          const isCollapsed = group.label ? collapsedSections[group.label] : false;
          // Sprint 1070.C5-prep5 — query-aware active match per group.
          const groupActivePath = bestActiveLinkInGroup(group.items, location.pathname, location.search);
          const hasActiveChild = !!groupActivePath;
          const isReadOnly = group.module && perms[group.module] === "read";
          const gColor = group.label ? groupColors[group.label] || sc.groupLabel : null;
          return (
            <div key={gi} className={gi > 0 ? "mt-2" : ""}>
              {group.label ? (
                <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${sc.border}`, backgroundColor: `${gColor}04` }}>
                  <button
                    onClick={() => toggleSection(group.label)}
                    className="w-full flex items-center justify-between cursor-pointer px-3 py-2 text-[10px] font-semibold uppercase tracking-wider"
                    style={{ color: gColor, borderBottom: !isCollapsed ? `1px solid ${sc.border}` : 'none' }}
                    data-testid={`sidebar-group-${group.label.toLowerCase().replace(/\s+/g, '-')}`}
                    data-sidebar-section-header
                  >
                    <span className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: gColor }} />
                      {t(sectionKey[group.label] || group.label)}
                      {isReadOnly && <span className="text-[9px] font-normal px-1 py-0 rounded bg-amber-500/15 text-amber-400 border border-amber-500/20">{t("VIEW")}</span>}
                    </span>
                    <svg className={`w-3 h-3 transition-transform duration-200 ${isCollapsed ? "-rotate-90" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {/* Sprint 1093-c — When the rail is collapsed, force all
                      items to render regardless of the group's open/closed
                      state. Otherwise closed-group icons disappear, which
                      paco perceived as the menu "reordering" between
                      expanded and collapsed views. The group section-header
                      is already hidden by CSS, so the rail just looks like
                      a flat icon column with stable order. */}
                  {(!isCollapsed || railCollapsed) && (
                    <div className="px-1 py-1 space-y-0.5">
                      {group.items.map((l, li) => {
                        if (l.separator) {
                          return (
                            <div key={`sep-${li}`} className="flex items-center gap-2 px-2 pt-2.5 pb-0.5" data-sidebar-separator>
                              <span className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: sc.textMuted }}>{t(sepKey[l.label] || l.label)}</span>
                              <div className="flex-1 h-px" style={{ backgroundColor: sc.border }} />
                            </div>
                          );
                        }
                        const isActive = groupActivePath === l.path;
                        return (
                          <Link key={l.path} to={l.path}
                            title={isActive ? buildYouAreHereTooltip(t(labelKey[l.label] || l.label), l.path) : t(labelKey[l.label] || l.label)}
                            className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-[13px] font-medium transition-all min-w-0"
                            style={{
                              color: isActive ? gColor : sc.textSecondary,
                              backgroundColor: isActive ? `${gColor}15` : "transparent",
                              borderLeft: isActive ? `2px solid ${gColor}` : "2px solid transparent",
                              paddingLeft: isActive ? "8px" : "10px",
                            }}
                            data-testid={`sidebar-link-${l.path.slice(1).replace(/[?&=/]/g, "-")}`}
                            data-active={isActive || undefined}
                            data-you-are-here={isActive ? buildYouAreHereTooltip(t(labelKey[l.label] || l.label), l.path) : undefined}
                            data-sidebar-link={l.path}>
                            <svg className="w-[16px] h-[16px] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={l.icon} />
                            </svg>
                            <span className="truncate">{t(labelKey[l.label] || l.label)}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                  {isCollapsed && hasActiveChild && !railCollapsed && (
                    <div className="px-1 py-1 space-y-0.5">
                      {group.items.filter(l => !l.separator && groupActivePath === l.path).map((l) => (
                        <Link key={l.path} to={l.path}
                          title={t(labelKey[l.label] || l.label)}
                          className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-[13px] font-medium min-w-0"
                          style={{ color: gColor, backgroundColor: `${gColor}15` }}
                          data-testid={`sidebar-link-collapsed-${l.path.slice(1).replace(/[?&=/]/g, "-")}`}
                          data-active="true"
                          data-sidebar-link={l.path}>
                          <svg className="w-[16px] h-[16px] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={l.icon} />
                          </svg>
                          <span className="truncate">{t(labelKey[l.label] || l.label)}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                /* Dashboard (ungrouped) */
                <div className="space-y-0.5">
                  {group.items.map((l) => {
                    const isActive = groupActivePath === l.path;
                    return (
                      <Link key={l.path} to={l.path}
                        title={isActive ? buildYouAreHereTooltip(t(labelKey[l.label] || l.label), l.path) : t(labelKey[l.label] || l.label)}
                        className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-colors min-w-0"
                        style={{
                          color: isActive ? sc.linkActive : sc.textSecondary,
                          backgroundColor: isActive ? sc.linkActiveBg : "transparent",
                          borderLeft: isActive ? `2px solid ${sc.linkActive}` : "2px solid transparent",
                          paddingLeft: isActive ? "10px" : "12px",
                        }}
                        data-testid={`sidebar-link-${l.path.slice(1).replace(/[?&=/]/g, "-")}`}
                        data-active={isActive || undefined}
                        data-you-are-here={isActive ? buildYouAreHereTooltip(t(labelKey[l.label] || l.label), l.path) : undefined}
                        data-sidebar-link={l.path}>
                        <svg className="w-[18px] h-[18px] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={l.icon} />
                        </svg>
                        <span className="truncate">{t(labelKey[l.label] || l.label)}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Online team members — hidden when the rail is collapsed (avatars overflow). */}
      {!railCollapsed && <PresenceIndicator onlineUsers={presenceUsers} currentUserId={user?.user_id} />}

      {/* Language, Sidebar Theme & Color Theme — 2-column layout.            */}
      {/* Sprint 1090 follow-up — Hidden entirely when rail is collapsed       */}
      {/* (these controls require labels that don't fit in 64px). Paco can    */}
      {/* still toggle via the chevron at the top of the rail.                */}
      {/* Sprint 1093-b — Collapsed-state mini icon bar.
          Replaces the previously-hidden bottom panel so paco can still
          flip Language / Sidebar variant / Color theme from the 64px rail.
          Each icon: 36px tap target · cycles its option on click ·
          tooltip surfaces the current value. */}
      {railCollapsed && (
        <div
          className="px-2 py-2 flex flex-col items-center gap-1.5"
          style={{ borderTop: `1px solid ${sc.border}` }}
          data-testid="sidebar-collapsed-quick-actions"
        >
          {/* Language cycler — EN → ES → DE → EN */}
          <button
            type="button"
            onClick={() => {
              const order = ["en", "es", "de"];
              const next = order[(order.indexOf(language) + 1) % order.length];
              setLanguage(next);
            }}
            title={`${t("sidebar.language_label")}: ${(language || "en").toUpperCase()}`}
            className="w-9 h-9 rounded-lg flex flex-col items-center justify-center transition-colors hover:bg-[var(--app-surface)]"
            style={{ color: sc.textSecondary }}
            data-testid="sidebar-collapsed-lang-btn"
          >
            <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="1.7" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="9" />
              <path d="M3 12h18M12 3a14 14 0 010 18M12 3a14 14 0 000 18" strokeLinecap="round" />
            </svg>
            <span className="text-[7px] font-bold mt-0.5 leading-none tracking-wider" style={{ color: sc.text }}>
              {(language || "en").toUpperCase()}
            </span>
          </button>

          {/* Sidebar variant cycler — Auto → Dark → Light → Auto */}
          <button
            type="button"
            onClick={() => {
              const order = ["default", "dark", "light"];
              const next = order[(order.indexOf(sidebarThemeName) + 1) % order.length];
              setSidebarTheme(next);
            }}
            title={`${t("sidebar.sidebar_label") || "Side Bar"}: ${sidebarThemeName === "default" ? "Auto" : sidebarThemeName === "dark" ? "Dark" : "Light"}`}
            className="w-9 h-9 rounded-lg flex items-center justify-center transition-colors hover:bg-[var(--app-surface)]"
            style={{ color: sc.textSecondary }}
            data-testid="sidebar-collapsed-variant-btn"
          >
            <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="1.7" viewBox="0 0 24 24">
              <rect x="3" y="4" width="18" height="16" rx="2" />
              <path d="M9 4v16" strokeLinecap="round" />
              <rect x="4.5" y="5.5" width="3" height="13" rx="0.5" fill="currentColor" opacity="0.35" stroke="none" />
            </svg>
          </button>

          {/* Color-theme toggle — Dark ↔ Light */}
          <button
            type="button"
            onClick={toggleTheme}
            title={themeName === "dark" ? "Switch to Light Mode" : "Switch to Dark Mode"}
            className="w-9 h-9 rounded-lg flex items-center justify-center transition-colors hover:bg-[var(--app-surface)]"
            style={{ color: sc.textSecondary }}
            data-testid="sidebar-collapsed-theme-btn"
          >
            {themeName === "dark" ? (
              <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="1.7" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="4" />
                <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" strokeLinecap="round" />
              </svg>
            ) : (
              <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="1.7" viewBox="0 0 24 24">
                <path d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </button>
        </div>
      )}

      {!railCollapsed && (
      <div className="px-4 py-2 space-y-2" style={{ borderTop: `1px solid ${sc.border}` }}>
        {/* Row 1: Language */}
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider mb-1 px-1" style={{ color: sc.textMuted }}>{t("sidebar.language_label")}</p>
          <div className="flex items-center justify-between rounded-lg p-1 gap-0.5" style={{ backgroundColor: sc.surface }}>
            {[["en","EN"],["es","ES"],["de","DE"]].map(([code, label]) => (
              <button key={code} onClick={() => setLanguage(code)}
                className="flex-1 text-xs font-medium py-1.5 rounded-md transition-colors"
                style={{ backgroundColor: language === code ? sc.bg : "transparent", color: language === code ? sc.text : sc.textMuted }}
                data-testid={`lang-${code}-btn`}>{label}</button>
            ))}</div>
        </div>
        {/* Row 2: Side Bar + Color Theme side by side */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider mb-1 px-1" style={{ color: sc.textMuted }}>{t("sidebar.sidebar_label") || "Side Bar"}</p>
            <div className="flex items-center gap-0.5 rounded-lg p-0.5" style={{ backgroundColor: sc.surface }}>
              {["default", "dark", "light"].map(st => (
                <button key={st} onClick={() => setSidebarTheme(st)}
                  className="flex-1 text-[9px] font-medium py-1.5 rounded-md transition-colors capitalize"
                  style={{ backgroundColor: sidebarThemeName === st ? sc.bg : "transparent", color: sidebarThemeName === st ? sc.text : sc.textMuted }}
                  data-testid={`sidebar-theme-${st}`}>{st === "default" ? "Auto" : st === "dark" ? t("Dark") : t("Light")}</button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider mb-1 px-1" style={{ color: sc.textMuted }}>{t("sidebar.color_theme_label")}</p>
            <button onClick={toggleTheme}
              className="w-full flex items-center justify-center gap-1.5 text-[10px] font-medium py-[7px] rounded-lg transition-colors"
              style={{ backgroundColor: sc.surface, color: sc.textSecondary }}
              data-testid="theme-toggle-btn">
              {themeName === "dark" ? (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
              ) : (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
              )}
              {themeName === "dark" ? t("sidebar.light_mode") : "Dark Mode"}
            </button>
          </div>
        </div>
      </div>
      )}

      {user && (
        <div className={`${railCollapsed ? "p-2" : "p-4"} transition-[padding] duration-200`} style={{ borderTop: `1px solid ${sc.border}` }}>
          {!railCollapsed && <SidebarAISpendChip sc={sc} />}
          <button onClick={() => setShowProfileModal(true)} className={`flex items-center ${railCollapsed ? "justify-center" : "gap-3"} w-full text-left rounded-xl hover:bg-[var(--app-surface)] ${railCollapsed ? "p-1" : "p-1.5 -m-1.5"} transition-colors group`} data-testid="sidebar-profile-btn" title={railCollapsed ? user.name : undefined}>
            {user.picture ? (
              <img src={user.picture} alt="" className="w-10 h-10 rounded-full shrink-0" style={{ boxShadow: `0 0 0 2px ${sc.border}` }} />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--app-accent)] to-[#BF5AF2] flex items-center justify-center text-white font-semibold shrink-0">{user.name?.[0]}</div>
            )}
            {!railCollapsed && (
              <>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: sc.text }}>{user.name}</p>
                  <p className="text-xs truncate" style={{ color: sc.textMuted }}>{user.email}</p>
                </div>
                <svg className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: sc.textMuted }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
              </>
            )}
          </button>
        </div>
      )}
      {showProfileModal && user && (
        <UserProfileModal
          user={user}
          onClose={() => setShowProfileModal(false)}
          onUpdated={(updated) => { setUser(prev => ({ ...prev, ...updated })); }}
        />
      )}
      </aside>
    </>
  );
};

// Keep the import below (rest of file unchanged).
import { usePresence } from "../hooks/usePresence";
import { PresenceProvider } from "../contexts/PresenceContext";

const Layout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { theme, themeName } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.is_super_admin === true;
  const { t, fmt } = useLanguage();
  const { onlineUsers, typingUsers, sendCursor, sendTyping, sendTypingStopped } = usePresence(user?.user_id);
  const presenceValue = { onlineUsers, typingUsers, sendCursor, sendTyping, sendTypingStopped };

  // Sprint 1070.C5-followup — Track previous pathname so the Mission Pulse
  // heatmap can pulse a "📍 You are here" marker on the stage the tenant
  // last visited. Persists across navigation via sessionStorage.
  useEffect(() => {
    return () => {
      try {
        const here = location.pathname;
        if (here && here !== "/dashboard") sessionStorage.setItem("sp:journey-prev-path", here);
      } catch { /* noop */ }
    };
  }, [location.pathname]);

  const [bellData, setBellData] = useState(null);
  const [bellOpen, setBellOpen] = useState(false);
  const [bellItems, setBellItems] = useState([]);
  const [bellTab, setBellTab] = useState("all");
  const [pipelineNotifs, setPipelineNotifs] = useState([]);
  const [pipelineUnread, setPipelineUnread] = useState(0); // "all" | "live" | "proposals" | "system"
  const [proposalActivity, setProposalActivity] = useState([]);
  const [liveProposals, setLiveProposals] = useState([]);
  const [chatUnread, setChatUnread] = useState(0);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatConvos, setChatConvos] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [remindersOpen, setRemindersOpen] = useState(false);
  const [reminders, setReminders] = useState([]);
  const overdueCount = reminders.filter((r) => r.is_overdue).length;
  const upcomingCount = reminders.filter((r) => !r.is_overdue).length;
  const bellRef = useRef(null);
  const chatRef = useRef(null);
  const remindersRef = useRef(null);
  // Sprint 1069.E1-followup-3 — Cross-Studio ⌘. Glance modal.
  const [glanceOpen, setGlanceOpen] = useState(false);

  // Global ⌘. (Cmd+Period) / Ctrl+Period to open Glance from anywhere.
  // Skips when focus is inside a text input or contenteditable so the
  // shortcut never collides with active typing.
  useEffect(() => {
    if (!user) return;
    const onKey = (e) => {
      const isPeriod = e.key === "." || e.code === "Period";
      const cmdOrCtrl = e.metaKey || e.ctrlKey;
      if (!isPeriod || !cmdOrCtrl) return;
      // Don't hijack inside inputs / textareas / contenteditable.
      const tgt = e.target;
      const tag = (tgt?.tagName || "").toLowerCase();
      const inField = tag === "input" || tag === "textarea" || tgt?.isContentEditable;
      if (inField) return;
      e.preventDefault();
      setGlanceOpen(o => !o);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [user]);

  // Click-outside handler for dropdowns
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (bellOpen && bellRef.current && !bellRef.current.contains(e.target)) setBellOpen(false);
      if (chatOpen && chatRef.current && !chatRef.current.contains(e.target)) setChatOpen(false);
      if (remindersOpen && remindersRef.current && !remindersRef.current.contains(e.target)) setRemindersOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [bellOpen, chatOpen, remindersOpen]);

  // Admin presence heartbeat (every 60s)
  useEffect(() => {
    if (!user) return;
    const beat = () => api.post("/presence/heartbeat").catch(() => {});
    beat();
    const hb = setInterval(beat, 60000);
    return () => clearInterval(hb);
  }, [user]);

  // Poll for unread chat messages
  useEffect(() => {
    if (!user) return;
    const fetchUnread = () => {
      api.get("/chat/total-unread").then(r => setChatUnread(r.data?.total_unread || 0)).catch(() => {});
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 15000);
    return () => clearInterval(interval);
  }, [user]);

  // Fetch reminders for header dropdown
  useEffect(() => {
    if (!user) return;
    const fetchReminders = () => {
      api.get("/reminders").then(r => setReminders(r.data || [])).catch(() => {});
    };
    fetchReminders();
    const interval = setInterval(fetchReminders, 60000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const fetchBell = () => {
      api.get("/notifications/bell-summary").then(r => setBellData(r.data)).catch(() => {});
    };
    fetchBell();
    const interval = setInterval(fetchBell, 60000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    if (bellOpen && user) {
      api.get("/notifications?unread_only=false").then(r => setBellItems(r.data?.notifications || [])).catch(() => {});
      api.get("/proposals-v2/activity/feed").then(r => {
        setProposalActivity(r.data?.activity || []);
        setLiveProposals(r.data?.live_now || []);
      }).catch(() => {});
      api.get("/notifications/pipeline?limit=30").then(r => {
        setPipelineNotifs(r.data?.notifications || []);
        setPipelineUnread(r.data?.unread || 0);
      }).catch(() => {});
    }
  }, [bellOpen, user]);

  // Global SSE for real-time proposal activity
  useEffect(() => {
    if (!user) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const es = new EventSource(`${backendUrl}/api/proposals-v2/activity/live-stream`);
    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === "snapshot") {
          setLiveProposals(data.live_proposals || []);
        } else if (data.type === "live_view") {
          setLiveProposals(prev => {
            const existing = prev.find(p => p.proposal_id === data.proposal_id);
            if (existing) return prev.map(p => p.proposal_id === data.proposal_id ? { ...p, viewer_count: (p.viewer_count || 0) + 1 } : p);
            return [{ proposal_id: data.proposal_id, proposal_name: data.proposal_name, client_name: data.client_name, viewer_count: 1 }, ...prev];
          });
        } else if (data.type === "proposal_viewed") {
          setProposalActivity(prev => [{
            id: `view_live_${Date.now()}`, type: "proposal_viewed", title: "Proposal viewed",
            message: `'${data.proposal_name}' opened by ${data.client_name || 'a client'}`,
            proposal_id: data.proposal_id, timestamp: data.timestamp,
            link: `/proposal-builder/${data.proposal_id}`, priority: "high",
          }, ...prev.slice(0, 29)]);
        } else if (data.type === "smart_timing") {
          setProposalActivity(prev => [{
            id: `timing_${Date.now()}`, type: "smart_timing", title: "Best time to call!",
            message: data.message || `Client engaged with '${data.proposal_name}'`,
            proposal_id: data.proposal_id, timestamp: data.timestamp,
            link: `/proposal-builder/${data.proposal_id}`, priority: "high",
          }, ...prev.slice(0, 29)]);
          // Also show a toast for urgency
          if (typeof window !== "undefined") {
            import("sonner").then(m => m.toast.success(`${data.client_name || "Client"}: ${data.scroll_depth}% — ${data.proposal_name}`, { duration: 10000 }));
          }
        } else if (data.type === "quote_viewed") {
          setProposalActivity(prev => [{
            id: `qview_${Date.now()}`, type: "quote_viewed", title: "Quote viewed",
            message: `${data.client_name || "Client"} opened Quote ${data.quote_number || ""}`,
            quote_id: data.quote_id, timestamp: data.timestamp,
            link: `/quotes/${data.quote_id}`, priority: "high",
          }, ...prev.slice(0, 29)]);
          // Real-time toast for urgency
          if (typeof window !== "undefined") {
            import("sonner").then(m => m.toast(`${data.client_name || "A client"} is viewing Quote ${data.quote_number || ""}`, { duration: 8000, icon: "👁" }));
          }
        } else if (data.type === "quote_approved") {
          setProposalActivity(prev => [{
            id: `qapprove_${Date.now()}`, type: "quote_approved", title: "Quote approved!",
            message: `${data.client_name || "Client"} approved Quote ${data.quote_number || ""}`,
            quote_id: data.quote_id, timestamp: data.timestamp,
            link: `/quotes/${data.quote_id}`, priority: "high",
          }, ...prev.slice(0, 29)]);
          if (typeof window !== "undefined") {
            import("sonner").then(m => m.toast.success(`${data.quote_number || ""} ✓ ${data.client_name || "client"}`, { duration: 15000 }));
          }
        }
      } catch {}
    };
    return () => es.close();
  }, [user]);

  // Pipeline WebSocket for real-time deal notifications
  useEffect(() => {
    if (!user) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const wsUrl = backendUrl.replace("https://", "wss://").replace("http://", "ws://");
    let ws = null;
    let reconnectTimer = null;
    const connect = () => {
      ws = new WebSocket(`${wsUrl}/api/ws/pipeline/${user.user_id || "anon"}`);
      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          const evtType = data.type;
          const evtData = data.data || {};
          const labels = {
            new_lead: "New Lead",
            deal_won: "Deal Won!",
            deal_lost: "Deal Lost",
            deal_stage_change: "Stage Changed",
            contract_signed: "Contract Signed!",
            contract_partially_signed: "Contract Partially Signed",
            invoice_paid: "Invoice Paid!",
            proposal_viewed: "Proposal Viewed",
            email_health_alert: "Email Health Alert",
          };
          const title = labels[evtType] || "Pipeline Update";
          const clientName = evtData.client || evtData.client_name || evtData.lead_id || "";
          // CURRENCY-FTM-01: timeline money is a transaction amount → use the event's
          // own currency; if the audit payload omits it, render the safe state (no naked
          // number). Audit-event currency enrichment is tracked in C2.
          const value = (evtData.value || evtData.amount) ? `${fmt.moneyShort(Number(evtData.value || evtData.amount), evtData.currency)}` : "";
          const msg = evtType === "email_health_alert" ? (evtData.message || `Bounce rate alert for ${evtData.category}`) : [clientName, value].filter(Boolean).join(" — ");
          // Add to activity feed
          setProposalActivity(prev => [{
            id: `pipe_${Date.now()}`, type: evtType, title,
            message: msg, timestamp: data.timestamp, priority: evtType === "deal_won" ? "high" : "medium",
          }, ...prev.slice(0, 29)]);
          // Increment pipeline unread counter
          setPipelineUnread(prev => prev + 1);
          // Toast — only show if there's meaningful content
          if (typeof window !== "undefined" && msg) {
            const toastFn = ["deal_won", "contract_signed", "invoice_paid"].includes(evtType) ? "success" : evtType === "deal_lost" ? "error" : evtType === "email_health_alert" ? "warning" : "info";
            import("sonner").then(m => (m.toast[toastFn] || m.toast)(`${title}: ${msg}`, { duration: 8000 }));
          }
        } catch {}
      };
      ws.onclose = () => { reconnectTimer = setTimeout(connect, 10000); };
      ws.onerror = () => { ws?.close(); };
    };
    connect();
    return () => { ws?.close(); clearTimeout(reconnectTimer); };
  }, [user]);

  // Fetch chat conversations when modal opens
  useEffect(() => {
    if (chatOpen && user) {
      setChatLoading(true);
      api.get("/chat/conversations").then(r => setChatConvos(r.data || [])).catch(() => {}).finally(() => setChatLoading(false));
    }
  }, [chatOpen, user]);

  const timeAgo = (iso) => {
    if (!iso) return "";
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  const markAllRead = () => {
    api.post("/notifications/mark-read", {}).then(() => {
      setBellData(prev => prev ? { ...prev, unread_notifications: 0, total: (prev.total || 0) - (prev.unread_notifications || 0) } : prev);
      setBellItems(prev => prev.map(n => ({ ...n, read: true })));
    }).catch(() => {});
  };

  // Determine active section color from current path
  const sectionColorMap = {
    "/leads": "var(--app-blue)", "/briefs": "var(--app-blue)", "/quotes": "var(--app-blue)", "/kanban": "var(--app-blue)",
    "/contacts": "var(--app-blue)", "/clients": "var(--app-blue)", "/intake-forms": "var(--app-blue)", "/calendar": "var(--app-blue)", "/meetings": "var(--app-blue)",
    "/proposal-builder": "var(--app-purple)", "/one-page-pitches": "var(--app-purple)", "/packages": "var(--app-purple)", "/proposal-analytics": "var(--app-purple)",
    "/administration/health": "var(--app-blue)", "/analytics": "var(--app-blue)",
    "/marketing": "var(--app-pink)",
    "/marketing/dashboard": "var(--app-pink)",
    "/marketing/research": "var(--app-pink)",
    "/marketing/social": "var(--app-pink)",
    "/marketing/social-hub": "var(--app-pink)",
    "/creative-studio": "var(--app-pink)",
    "/creative-hub": "var(--app-pink)",
    "/marketing/website": "var(--app-pink)",
    // Sprint 1060a — Web Studio shares the marketing pink accent.
    "/web-studio": "var(--app-pink)",
    "/marketing/brand-setup": "var(--app-pink)",
    "/marketing/brand-launch": "var(--app-amber)",
    "/marketing/diagnostic": "var(--app-pink)",
    "/marketing/brand-dashboard": "var(--app-pink)",
    "/marketing/plan": "var(--app-pink)",
    "/marketing/roi": "var(--app-green)",
    "/marketing/brand-repository": "var(--app-pink)",
    "/marketing/seo-analyzer": "var(--app-pink)",
    "/marketing/ai-positioning": "var(--app-pink)",
    "/marketing/competitor-intel": "var(--app-pink)",
    "/marketing/email-templates": "var(--app-pink)",
    "/catalog": "var(--app-green)",
    "/industry-templates": "var(--app-green)",
    "/projects": "var(--app-purple)", "/crew-resources": "var(--app-purple)", "/my-tasks": "var(--app-purple)",
    "/project-calendar": "var(--app-purple)", "/reports": "var(--app-purple)",
    "/projects/gantt": "var(--app-purple)",
    "/administration": "var(--app-amber)", "/invoices": "var(--app-amber)", "/contracts": "var(--app-amber)",
    "/rate-cards": "var(--app-amber)", "/rate-intelligence": "var(--app-amber)", "/reporting": "var(--app-amber)", "/crew": "var(--app-amber)", "/crew/skill-matrix": "var(--app-amber)",
    "/settings": "var(--app-text-muted)", "/email-ingest": "var(--app-text-muted)", "/terms": "var(--app-text-muted)", "/company-branding": "var(--app-text-muted)", "/calendar/subscriptions": "#A855F7", "/super-admin": "#f43f5e",
    "/platform/system-audit": "#f43f5e", "/platform/component-map": "#f43f5e", "/platform/email-logs": "#f43f5e",
    "/platform/localization": "#f43f5e",
    "/platform/monetization": "#f43f5e",
    "/platform/support-tickets": "#f43f5e",
    "/platform/ops-dashboard": "#f43f5e",
    "/platform/admin-index": "#f43f5e",
    "/platform/permissions": "#f43f5e", "/platform/ai-usage": "#f43f5e", "/platform/storage": "#f43f5e",
    "/platform": "#f43f5e",
  };
  const path = location.pathname;
  const activeColor = sectionColorMap[path] || Object.entries(sectionColorMap).find(([k]) => path.startsWith(k + "/"))?.[1] || null;
  const bellTotal = (bellData?.total || 0) + liveProposals.length + pipelineUnread;

  // Derive page ID for contextual help from current route
  const currentPageId = (() => {
    const p = location.pathname.replace(/^\//, "").replace(/\/$/, "");
    const segments = p.split("/");
    if (segments[0] === "platform" && segments[1]) return segments[1];
    if (segments[0] === "marketing" && segments[1]) return segments[1];
    return segments[0] || "dashboard";
  })();

  return (
    <WalkthroughProvider>
    <EngagementAlertsListener />
    {/* CompassProgressBadge is now rendered inline inside the Dev Library
        header (Compass · Phase D · 2026-05-25). Keeping it removed from the
        global shell so it stops overlapping the budget pill across the app. */}
    <div className="flex min-h-screen" style={{ background: theme.background }}>
      <Sidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} presenceUsers={onlineUsers} />
      <main className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
        {/* Desktop sticky header bar */}
        <div className="hidden lg:flex items-center gap-3 px-8 py-3 border-b border-[var(--app-border)] flex-shrink-0 relative z-30" style={{ background: theme.background }} data-testid="desktop-header">
          {/* Section color accent strip */}
          {activeColor && (
            <div className="absolute left-0 top-0 right-0 h-0.5" style={{ background: `linear-gradient(90deg, ${activeColor}, ${activeColor}00)` }} data-testid="section-accent-strip" />
          )}
          <div className="flex-1">
            <GlobalSearch />
          </div>
          {/* Brand Switcher — only visible for multi-brand users */}
          <BrandSwitcher />
          {/* Centered App Logo — absolute positioning for true center */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none" data-testid="header-app-logo">
            <img
              src={themeName === "dark" ? "/studio-plaz-logo-white.png" : "/studio-plaz-logo-blue.png"}
              alt="Studio Plaz"
              className="h-8 w-auto"
            />
          </div>
          {/* Reminders Dropdown */}
          <div className="relative" ref={remindersRef}>
            <button
              onClick={() => { setRemindersOpen(!remindersOpen); setBellOpen(false); setChatOpen(false); }}
              className="relative p-2 rounded-xl hover:bg-[var(--app-surface)] transition-colors"
              data-testid="header-reminders"
              title={t("sidebar.reminders") || "Reminders"}
            >
              <svg className="w-5 h-5 text-[var(--app-text-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {(overdueCount + upcomingCount > 0) && (
                <span className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center text-white ${overdueCount > 0 ? "bg-[var(--app-danger)] animate-pulse" : "bg-amber-500"}`} data-testid="reminders-count">
                  {overdueCount + upcomingCount > 99 ? "99+" : overdueCount + upcomingCount}
                </span>
              )}
            </button>
            {remindersOpen && (
                <div className="absolute right-0 top-full mt-2 w-96 max-h-[480px] overflow-hidden rounded-2xl border border-[var(--app-border)] shadow-2xl z-50" style={{ backgroundColor: 'var(--app-bg)' }} data-testid="reminders-dropdown">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--app-border)]">
                    <h3 className="text-sm font-semibold text-[var(--app-text)]">{t("sidebar.reminders")}</h3>
                    <span className="text-[10px] text-[var(--app-text-muted)]">{reminders.length} {t("bell.total")}</span>
                  </div>
                  {/* Summary */}
                  <div className="grid grid-cols-2 gap-2 p-3 border-b border-[var(--app-border)]">
                    <div className="text-center p-3 rounded-xl border border-red-500/20 bg-red-500/5">
                      <svg className="w-5 h-5 mx-auto mb-1 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                      <p className="text-xl font-bold text-red-500">{overdueCount}</p>
                      <p className="text-[9px] text-[var(--app-text-muted)] uppercase font-medium tracking-wide">{t("sidebar.overdue")}</p>
                    </div>
                    <div className="text-center p-3 rounded-xl border border-amber-500/20 bg-amber-500/5">
                      <svg className="w-5 h-5 mx-auto mb-1 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      <p className="text-xl font-bold text-amber-500">{upcomingCount}</p>
                      <p className="text-[9px] text-[var(--app-text-muted)] uppercase font-medium tracking-wide">{t("sidebar.upcoming")}</p>
                    </div>
                  </div>
                  <div className="overflow-y-auto max-h-[320px]">
                    {reminders.length === 0 ? (
                      <p className="text-center py-8 text-sm text-[var(--app-text-muted)]">{t("sidebar.no_reminders")}</p>
                    ) : (
                      <>
                        {overdueCount > 0 && (
                          <div className="px-4 py-2 border-b border-[var(--app-border)]">
                            <p className="text-[10px] uppercase tracking-wider font-semibold text-red-400">{t("sidebar.overdue")} ({overdueCount})</p>
                          </div>
                        )}
                        {reminders.filter(r => r.is_overdue).slice(0, 8).map((r, i) => (
                          <button key={`o-${i}`} onClick={() => { setRemindersOpen(false); navigate(`/leads/${r.lead_id}`); }} className="w-full text-left block px-4 py-3 border-b border-[var(--app-border)] hover:bg-[var(--app-surface)] transition-colors bg-red-500/5" data-testid={`reminder-overdue-${i}`}>
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-red-500/10">
                                <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-[var(--app-text)] truncate">{r.lead_name}</p>
                                <p className="text-[10px] text-[var(--app-text-muted)] truncate mt-0.5">{r.description}</p>
                              </div>
                            </div>
                          </button>
                        ))}
                        {upcomingCount > 0 && (
                          <div className="px-4 py-2 border-b border-[var(--app-border)]">
                            <p className="text-[10px] uppercase tracking-wider font-semibold text-amber-400">{t("sidebar.upcoming")} ({upcomingCount})</p>
                          </div>
                        )}
                        {reminders.filter(r => !r.is_overdue).slice(0, 8).map((r, i) => (
                          <button key={`u-${i}`} onClick={() => { setRemindersOpen(false); navigate(`/leads/${r.lead_id}`); }} className="w-full text-left block px-4 py-3 border-b border-[var(--app-border)] hover:bg-[var(--app-surface)] transition-colors" data-testid={`reminder-upcoming-${i}`}>
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-amber-500/10">
                                <svg className="w-4 h-4 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-[var(--app-text)] truncate">{r.lead_name}</p>
                                <p className="text-[10px] text-[var(--app-text-muted)] truncate mt-0.5">{r.description} · {r.date}</p>
                              </div>
                            </div>
                          </button>
                        ))}
                      </>
                    )}
                  </div>
                </div>
            )}
          </div>
          {/* Clock */}
          <LiveClock compact />
          {/* Chat Messages Modal */}
          <div className="relative" ref={chatRef}>
            <button
              onClick={() => { setChatOpen(!chatOpen); setBellOpen(false); setRemindersOpen(false); }}
              className="relative p-2 rounded-xl hover:bg-[var(--app-surface)] transition-colors"
              data-testid="header-chat-badge"
              title={t("sidebar.chat_messages") || "Chat Messages"}
            >
              <svg className="w-5 h-5 text-[var(--app-text-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              {chatUnread > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center text-white bg-[var(--app-accent)] animate-pulse" data-testid="chat-unread-count">
                  {chatUnread > 99 ? "99+" : chatUnread}
                </span>
              )}
            </button>
            {chatOpen && (
                <div className="absolute right-0 top-full mt-2 w-96 max-h-[480px] overflow-hidden rounded-2xl border border-[var(--app-border)] shadow-2xl z-50" style={{ backgroundColor: 'var(--app-bg)' }} data-testid="chat-dropdown">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--app-border)]">
                    <h3 className="text-sm font-semibold text-[var(--app-text)]">{t("sidebar.chat_messages") || "Messages"}</h3>
                    <button onClick={() => { setChatOpen(false); navigate("/chat/conversations"); }} className="text-[10px] text-blue-500 hover:text-blue-400 font-medium" data-testid="chat-view-all">{t("bell.view_all") || "View all"}</button>
                  </div>
                  <div className="overflow-y-auto max-h-[400px]">
                    {chatLoading ? (
                      <div className="flex justify-center py-8">
                        <svg className="w-5 h-5 animate-spin text-[var(--app-text-muted)]" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                      </div>
                    ) : chatConvos.length === 0 ? (
                      <p className="text-center py-8 text-sm text-[var(--app-text-muted)]">{t("sidebar.no_messages") || "No conversations yet"}</p>
                    ) : chatConvos.slice(0, 10).map(c => (
                      <button
                        key={c.project_id}
                        onClick={() => { setChatOpen(false); navigate(`/projects/${c.project_id}`, { state: { openChat: true } }); }}
                        className={`w-full block px-4 py-3 border-b border-[var(--app-border)] hover:bg-[var(--app-surface)] transition-colors text-left ${c.admin_unread > 0 ? "bg-blue-500/5" : ""}`}
                        data-testid={`chat-convo-${c.project_id}`}
                      >
                        <div className="flex items-start gap-2">
                          <span className={`w-2 h-2 mt-1.5 rounded-full flex-shrink-0 ${c.admin_unread > 0 ? "bg-[var(--app-accent)]" : "bg-transparent"}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-xs font-medium text-[var(--app-text)] truncate">{c.project_name}</p>
                              {c.admin_unread > 0 && (
                                <span className="min-w-[18px] h-[18px] px-1 rounded-full text-[9px] font-bold flex items-center justify-center text-white bg-[var(--app-accent)]">{c.admin_unread}</span>
                              )}
                            </div>
                            <p className="text-[10px] text-[var(--app-text-muted)] truncate mt-0.5">
                              {c.client_name && <span className="font-medium">{c.client_name} · </span>}
                              {c.last_message || "No messages"}
                            </p>
                          </div>
                          <span className="text-[9px] text-[var(--app-text-muted)] flex-shrink-0">{timeAgo(c.last_message_at)}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
            )}
          </div>
          {/* AI Credits Indicator */}
          <AiCreditsIndicator />
          {/* Active Generations Tray (Sprint 888 — P1-C) */}
          <ActiveJobsTray />
          {/* What's new drawer (Sprint 914) */}
          <ChangelogDrawer t={t} />
          {/* Notification Bell */}
          <div className="relative" ref={bellRef}>
            <button
              onClick={() => { setBellOpen(!bellOpen); setChatOpen(false); setRemindersOpen(false); }}
              className="relative p-2 rounded-xl hover:bg-[var(--app-surface)] transition-colors"
              data-testid="header-notification-bell"
            >
              <svg className="w-5 h-5 text-[var(--app-text-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              {liveProposals.length > 0 && (
                <span className="absolute top-0.5 right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-[var(--app-bg)] animate-pulse" data-testid="bell-live-dot" />
              )}
              {bellTotal > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center bg-red-500 animate-pulse" style={{ color: 'var(--app-surface)' }} data-testid="bell-count">
                  {bellTotal > 99 ? "99+" : bellTotal}
                </span>
              )}
            </button>
            {bellOpen && (
                <div className="absolute right-0 top-full mt-2 w-[440px] max-h-[600px] overflow-hidden rounded-2xl border border-[var(--app-border)] shadow-2xl z-50" style={{ backgroundColor: 'var(--app-bg)' }} data-testid="bell-dropdown">
                  {/* Header */}
                  <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--app-border)]">
                    <h3 className="text-sm font-semibold text-[var(--app-text)]">{t("sidebar.notifications")}</h3>
                    <div className="flex items-center gap-2">
                      {liveProposals.length > 0 && (
                        <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-[9px] font-bold text-emerald-400 animate-pulse" data-testid="bell-live-count">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                          {liveProposals.length} live
                        </span>
                      )}
                      <button onClick={markAllRead} className="text-[10px] text-blue-500 hover:text-blue-400 font-medium" data-testid="bell-mark-all-read">{t("sidebar.mark_all_read")}</button>
                    </div>
                  </div>

                  {/* Live Activity Banner — only when someone is viewing right now */}
                  {liveProposals.length > 0 && (
                    <div className="border-b border-emerald-500/20 bg-emerald-500/5" data-testid="bell-live-section">
                      <div className="px-4 py-2">
                        <p className="text-[10px] uppercase tracking-wider font-semibold text-emerald-400 flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                          Viewing Right Now
                        </p>
                      </div>
                      {liveProposals.map(lp => (
                        <button
                          key={lp.proposal_id}
                          onClick={() => { setBellOpen(false); navigate(`/proposal-builder/${lp.proposal_id}`); }}
                          className="w-full text-left px-4 py-2.5 hover:bg-emerald-500/10 transition-colors flex items-start gap-3"
                          data-testid={`bell-live-${lp.proposal_id}`}
                        >
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-emerald-500/15 border border-emerald-500/20">
                            <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-emerald-300 truncate">{lp.client_name || "Client"} is viewing</p>
                            <p className="text-[10px] text-[var(--app-text-muted)] truncate">{lp.proposal_name} &middot; {lp.viewer_count} viewer{lp.viewer_count > 1 ? "s" : ""}</p>
                          </div>
                          <span className="text-[9px] font-bold text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded">{t("LIVE")}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Tab Filter */}
                  <div className="flex items-center gap-1 px-3 py-2 border-b border-[var(--app-border)]">
                    {[
                      { key: "all", label: t("All") },
                      { key: "proposals", label: t("Proposals") },
                      { key: "quotes", label: t("Quotes") },
                      { key: "pipeline", label: "Pipeline" },
                      { key: "system", label: t("System") },
                    ].map(tab => (
                      <button
                        key={tab.key}
                        onClick={() => setBellTab(tab.key)}
                        className={`px-3 py-1 rounded-lg text-[10px] font-semibold transition-colors ${bellTab === tab.key ? "bg-blue-500/15 text-blue-400" : "text-[var(--app-text-muted)] hover:bg-[var(--app-surface)]"}`}
                        data-testid={`bell-tab-${tab.key}`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Summary Cards */}
                  {bellData && bellTab !== "proposals" && bellTab !== "quotes" && (
                    <div className="grid grid-cols-5 gap-2 p-3 border-b border-[var(--app-border)]">
                      <div className="text-center p-2.5 rounded-xl border border-amber-500/20 bg-amber-500/5" data-testid="bell-approvals-card">
                        <p className="text-lg font-bold text-amber-500">{bellData.pending_approvals}</p>
                        <p className="text-[8px] text-[var(--app-text-muted)] uppercase font-medium tracking-wide">{t("bell.approvals")}</p>
                      </div>
                      <div className="text-center p-2.5 rounded-xl border border-red-500/20 bg-red-500/5" data-testid="bell-overdue-card">
                        <p className="text-lg font-bold text-red-500">{bellData.overdue_tasks}</p>
                        <p className="text-[8px] text-[var(--app-text-muted)] uppercase font-medium tracking-wide">{t("bell.overdue")}</p>
                      </div>
                      <div className="text-center p-2.5 rounded-xl border border-blue-500/20 bg-blue-500/5" data-testid="bell-leads-card">
                        <p className="text-lg font-bold text-blue-500">{bellData.new_leads}</p>
                        <p className="text-[8px] text-[var(--app-text-muted)] uppercase font-medium tracking-wide">{t("bell.new_leads")}</p>
                      </div>
                      <div className="text-center p-2.5 rounded-xl border border-violet-500/20 bg-violet-500/5" data-testid="bell-proposal-views-card">
                        <p className="text-lg font-bold text-violet-500">{proposalActivity.filter(a => a.type === "proposal_viewed").length}</p>
                        <p className="text-[8px] text-[var(--app-text-muted)] uppercase font-medium tracking-wide">{t("Prop. Views")}</p>
                      </div>
                      <div className="text-center p-2.5 rounded-xl border border-cyan-500/20 bg-cyan-500/5" data-testid="bell-quote-views-card">
                        <p className="text-lg font-bold text-cyan-500">{proposalActivity.filter(a => a.type === "quote_viewed" || a.type === "quote_approved").length}</p>
                        <p className="text-[8px] text-[var(--app-text-muted)] uppercase font-medium tracking-wide">{t("Quote Views")}</p>
                      </div>
                    </div>
                  )}

                  {/* Feed */}
                  <div className="overflow-y-auto max-h-[340px]">
                    {/* Proposal Activity Items */}
                    {(bellTab === "all" || bellTab === "proposals" || bellTab === "quotes") && proposalActivity.length > 0 && (
                      <>
                        {bellTab !== "quotes" && proposalActivity.filter(a => !a.type?.startsWith("quote_")).length > 0 && (
                          <div className="px-4 py-2 border-b border-[var(--app-border)]">
                            <p className="text-[10px] uppercase tracking-wider font-semibold text-violet-400">{t("Proposal Activity")}</p>
                          </div>
                        )}
                        {bellTab === "quotes" && proposalActivity.filter(a => a.type?.startsWith("quote_")).length > 0 && (
                          <div className="px-4 py-2 border-b border-[var(--app-border)]">
                            <p className="text-[10px] uppercase tracking-wider font-semibold text-blue-400">{t("Quote Activity")}</p>
                          </div>
                        )}
                        {proposalActivity
                          .filter(a => {
                            if (bellTab === "proposals") return !a.type?.startsWith("quote_");
                            if (bellTab === "quotes") return a.type?.startsWith("quote_");
                            return true;
                          })
                          .slice(0, bellTab === "all" ? 8 : 20).map(a => {
                          const isSignoff = a.type === "proposal_signoff";
                          const isEngagement = a.type === "engagement_spike";
                          const isTiming = a.type === "smart_timing";
                          const isQuoteView = a.type === "quote_viewed";
                          const isQuoteApproved = a.type === "quote_approved";
                          const iconColor = isSignoff || isQuoteApproved ? "text-emerald-400" : isTiming ? "text-orange-400" : isEngagement ? "text-amber-400" : isQuoteView ? "text-blue-400" : "text-violet-400";
                          const bgColor = isSignoff || isQuoteApproved ? "bg-emerald-500/10" : isTiming ? "bg-orange-500/10 ring-1 ring-orange-500/20" : isEngagement ? "bg-amber-500/10" : isQuoteView ? "bg-blue-500/10" : "bg-violet-500/10";
                          const iconPath = isSignoff || isQuoteApproved
                            ? "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            : isTiming
                            ? "M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                            : isEngagement
                            ? "M13 10V3L4 14h7v7l9-11h-7z"
                            : isQuoteView
                            ? "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            : "M15 12a3 3 0 11-6 0 3 3 0 016 0z";
                          return (
                            <button
                              key={a.id}
                              onClick={() => { setBellOpen(false); if (a.link) navigate(a.link); else if (a.quote_id) navigate(`/quotes/${a.quote_id}`); }}
                              className="w-full text-left block px-4 py-3 border-b border-[var(--app-border)] hover:bg-[var(--app-surface)] transition-colors"
                              data-testid={`bell-activity-${a.id}`}
                            >
                              <div className="flex items-start gap-3">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${bgColor}`}>
                                  <svg className={`w-4 h-4 ${iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={iconPath} /></svg>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium text-[var(--app-text)] truncate">{a.title}</p>
                                  <p className="text-[10px] text-[var(--app-text-muted)] truncate mt-0.5">{a.message}</p>
                                  {a.timestamp && <p className="text-[9px] text-[var(--app-text-muted)] mt-1">{timeAgo(a.timestamp)}</p>}
                                </div>
                                {a.priority === "high" && <span className="w-1.5 h-1.5 rounded-full bg-violet-400 flex-shrink-0 mt-1.5" />}
                              </div>
                            </button>
                          );
                        })}
                      </>
                    )}

                    {/* Pipeline Notifications */}
                    {(bellTab === "all" || bellTab === "pipeline") && (
                      <>
                        {(() => {
                          const PIPE_TYPES = ["new_lead", "deal_won", "deal_lost", "deal_stage_change", "contract_signed", "contract_partially_signed", "invoice_paid", "email_health_alert"];
                          const memoryItems = proposalActivity.filter(a => PIPE_TYPES.includes(a.type));
                          const persistedItems = pipelineNotifs.filter(p => !memoryItems.find(m => m.id === p.notification_id));
                          const allPipeItems = [...memoryItems, ...persistedItems.map(p => ({
                            id: p.notification_id, type: p.type, title: p.title || p.type?.replace(/_/g, " "),
                            message: p.message || "", timestamp: p.timestamp || p.created_at, priority: p.priority || "medium", read: p.read,
                          }))];
                          if (allPipeItems.length === 0 && bellTab === "pipeline") {
                            return <p className="text-center py-8 text-sm text-[var(--app-text-muted)]">{t("No pipeline activity yet. Create or update leads to see real-time notifications.")}</p>;
                          }
                          if (allPipeItems.length === 0) return null;
                          return (
                            <>
                              <div className="px-4 py-2 border-b border-[var(--app-border)] flex items-center justify-between">
                                <p className="text-[10px] uppercase tracking-wider font-semibold text-[var(--app-text-muted)]">{t("Pipeline Activity")}</p>
                                {pipelineUnread > 0 && (
                                  <button onClick={() => { api.post("/notifications/pipeline/mark-read").then(() => setPipelineUnread(0)).catch(() => {}); }}
                                    className="text-[9px] text-blue-400 hover:underline" data-testid="bell-pipeline-mark-all-read">{t("Mark all read")}</button>
                                )}
                              </div>
                              {allPipeItems.slice(0, bellTab === "pipeline" ? 30 : 5).map(a => {
                                const icon = a.type === "deal_won" || a.type === "contract_signed" || a.type === "invoice_paid" ? "text-emerald-400" : a.type === "deal_lost" ? "text-red-400" : a.type === "new_lead" ? "text-blue-400" : a.type === "email_health_alert" ? "text-orange-400" : "text-amber-400";
                                const bgClass = icon.includes("emerald") ? "bg-emerald-500/10" : icon.includes("red") ? "bg-red-500/10" : icon.includes("blue") ? "bg-blue-500/10" : icon.includes("orange") ? "bg-orange-500/10" : "bg-amber-500/10";
                                return (
                                  <div key={a.id} className={`px-4 py-2.5 border-b border-[var(--app-border)] hover:bg-[var(--app-surface)] transition-colors ${!a.read ? "bg-blue-500/5" : ""}`} data-testid={`pipeline-notif-${a.id}`}>
                                    <div className="flex items-start gap-2">
                                      <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 ${bgClass}`}>
                                        <svg className={`w-3 h-3 ${icon}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-[11px] font-medium text-[var(--app-text)]">{a.title}</p>
                                        <p className="text-[9px] text-[var(--app-text-muted)] mt-0.5">{a.message}</p>
                                        {a.timestamp && <p className="text-[8px] text-[var(--app-text-muted)] mt-0.5">{timeAgo(a.timestamp)}</p>}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </>
                          );
                        })()}
                      </>
                    )}

                    {/* System Notifications */}
                    {(bellTab === "all" || bellTab === "system") && (
                      <>
                        <div className="px-4 py-2 border-b border-[var(--app-border)]">
                          <p className="text-[10px] uppercase tracking-wider font-semibold text-[var(--app-text-muted)]">{bellTab === "system" ? "System Notifications" : t("bell.recent_activity")}</p>
                        </div>
                        {bellItems.length === 0 ? (
                          <p className="text-center py-8 text-sm text-[var(--app-text-muted)]">{t("sidebar.no_notifications")}</p>
                        ) : bellItems.slice(0, bellTab === "system" ? 20 : 10).map(n => {
                          const typeIcon = n.type === "task_assigned" ? "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                            : n.type === "deadline_overdue" ? "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            : n.type === "budget_overrun" ? "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            : n.type === "project_created" ? "M12 6v6m0 0v6m0-6h6m-6 0H6"
                            : "M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9";
                          const typeColor = n.type === "task_assigned" ? "text-blue-400" : n.type === "deadline_overdue" ? "text-red-400" : n.type === "budget_overrun" ? "text-amber-400" : n.type === "project_created" ? "text-emerald-400" : "text-[var(--app-text-muted)]";
                          return (
                            <button key={n.notification_id} onClick={() => { setBellOpen(false); if (n.link && n.link !== "#") navigate(n.link); }} className={`w-full text-left block px-4 py-3 border-b border-[var(--app-border)] hover:bg-[var(--app-surface)] transition-colors ${!n.read ? "bg-blue-500/5" : ""}`} data-testid={`bell-item-${n.notification_id}`}>
                              <div className="flex items-start gap-3">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${!n.read ? "bg-blue-500/10" : "bg-[var(--app-surface)]"}`}>
                                  <svg className={`w-4 h-4 ${typeColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={typeIcon} /></svg>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className="text-xs font-medium text-[var(--app-text)] truncate">{n.title}</p>
                                    {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />}
                                  </div>
                                  <p className="text-[10px] text-[var(--app-text-muted)] truncate mt-0.5">{n.message}</p>
                                  {n.created_at && <p className="text-[9px] text-[var(--app-text-muted)] mt-1">{timeAgo(n.created_at)}</p>}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </>
                    )}

                    {bellTab === "proposals" && proposalActivity.length === 0 && (
                      <p className="text-center py-8 text-sm text-[var(--app-text-muted)]">{t("No proposal activity yet. Share a proposal to start tracking.")}</p>
                    )}
                  </div>
                </div>
            )}
          </div>
        </div>
        {/* Mobile header */}
        <div className="sticky top-0 z-30 lg:hidden flex items-center gap-3 px-4 py-3 border-b border-[var(--app-border)] flex-shrink-0" style={{ background: `${theme.background}E6`, backdropFilter: "blur(12px)" }}>
          <button onClick={() => setMobileOpen(true)} className="p-2 rounded-xl hover:bg-[var(--app-surface)]" data-testid="mobile-menu-btn">
            <svg className="w-5 h-5" style={{ color: theme.foreground }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
          </button>
          <div className="flex items-center gap-2 flex-1">
            <img src="/studio-plaz-icon.png" alt="Studio Plaz" className="w-7 h-7 rounded-lg" />
            <span className="text-sm font-semibold" style={{ color: theme.foreground }}>{t("Studio Plaz")}</span>
          </div>
          <button onClick={() => setBellOpen(!bellOpen)} className="relative p-2" data-testid="mobile-bell">
            <svg className="w-5 h-5 text-[var(--app-text-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>
            {bellTotal > 0 && <span className="absolute top-0 right-0 w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center text-white bg-red-500">{bellTotal > 9 ? "9+" : bellTotal}</span>}
          </button>
        </div>
        {/* Scrollable content area */}
        <PresenceProvider value={presenceValue}>
        {["/contracts"].some(p => location.pathname.startsWith(p)) || /^\/concept-room\/[^/]+/.test(location.pathname) || location.pathname.startsWith("/platform/dev-library") ? (
          <div className="flex-1 min-h-0 overflow-hidden">
            {children}
          </div>
        ) : location.pathname.startsWith("/kanban") ? (
          <div className="flex-1 overflow-auto">
            {/* Sprint 2026-06-12 PM-10.6 (paco: "breadcrumb does not load on /kanban").
                The /kanban branch was added to give KanbanPage full-width horizontal
                scroll, but it bypassed the global breadcrumb mounts. Pipeline is the
                first child of the Sales "Close & Kickoff" stage per canon, so the
                canonical 2-layer breadcrumb MUST render here too. Mounted inside a
                padded shell so it doesn't fight the kanban columns below. */}
            <div className="px-6 lg:px-8 pt-6">
              <SalesStudioPillarsBreadcrumb />
            </div>
            {children}
          </div>
        ) : (
        <div className="flex-1 overflow-auto">
          <div className="p-6 lg:p-8">
            <MarketplaceNewBanner />
            <TrialStatusBanner />
            <OperateAsBanner />
            <CloseDealBanner />
            {/* Sprint 2026-06-05 PM-7 · Canonical Brand Studio 3-row breadcrumb stack.
                Row 1 (Pillars) → Row 2 (Mission: Grow the Brand) → Row 3 (path crumb).
                Each component self-gates and only renders on /brand-studio/* paths. */}
            <BrandStudioPillarsBreadcrumb />
            {/* Sprint 2026-06-12 PM-10 (paco) — Sales Studio canonical
                breadcrumb mounts at the same Layout level as Brand so it
                sits ABOVE the page's gradient welcome header, mirroring
                paco's reference position from Brand Studio. Self-gates on
                /sales-studio + Sales leaf routes. */}
            <SalesStudioPillarsBreadcrumb />
            {/* Phase 1 · Marketing breadcrumb rollout (paco-approved). Self-gates
                on MARKETING_CANONICAL_PATHS. FlowBreadcrumb suppresses on the
                same paths so no 3rd row appears. */}
            <MarketingStudioPillarsBreadcrumb />
            {/* Sprint 1069.C — Universal Flow Breadcrumb (auto-detects mission/stage from pathname). */}
            <FlowBreadcrumb />
            {/* Sprint 2026-06-05 PM-6 · Brand Studio path-style breadcrumb
                (component is self-gated: returns null when not on a
                /brand-studio/* sub-route). Restores Row 3 of the canonical
                3-row breadcrumb stack per paco's 2026-06-02 screenshots. */}
            <BrandStudioBreadcrumb />
            {children}
          </div>
        </div>
        )}
        </PresenceProvider>
      </main>
      {user && <AiChat grounded={isSuperAdmin} />}
      {/* Sprint 1085 — Removed global <QuickWinsWidget />; unified JourneyHero on Mission Control owns the journey now. */}
      {user && <WalkthroughNudge />}
      {user && <ContextualHelp pageId={currentPageId} grounded={isSuperAdmin} />}
      {/* Sprint 1069.E1-followup-3 — Cross-Studio ⌘. Glance modal. */}
      {user && <GlanceModal open={glanceOpen} onClose={() => setGlanceOpen(false)} />}
    </div>
    </WalkthroughProvider>
  );
};

export default Layout;
